import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { TimePickerComponent } from '@syncfusion/ej2-react-calendars';
import AvailabilityViewer from './AvailabilityViewer.jsx';
import MultiRoomAvailabilityTable from './MultiRoomAvailabilityTable';
import PatientFormModal from './PatientFormModal';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';
import PatientSearchInput from './ui/PatientSearchInput';
import Breadcrumbs from '../erp-billing/Breadcrumbs';
import { bookingPlanService, searchProducts } from '../../core/services/bookingplanService';
import doctorServices from '../../core/services/doctorServices';
import roomServices from '../../core/services/roomServices';
import bookingplanService, { validateBookingPlan } from '../../core/services/bookingplanService';
import { doctorActions, patientActions } from '../../core/actions';
import { useDispatch } from 'react-redux';
import { resolveDoctorGvId } from '../../shared/helper/helper';
import { useOrganization } from '../../configurations/OrganizationContext';
import {
  DOCTOR_LEAVE_EARLY_TIMINGS,
  APPT_MORNING_TIMINGS,
  APPT_EVENING_TIMINGS,
  ROOM_CATEGORIES,
  Appointmant_schedule_session_booking_start_time,
  Appointmant_schedule_session_booking_end_time,
  Docter_leave_unavailable_slot_value,
  getOrgConstant,
  BOOKING_PLAN_GROUP_NEW_SESSION,
} from '../../shared/constants';

// const BOOKING_START_TIME = '06:00';
// const BOOKING_END_TIME   = '20:00';

// const pad = (n) => String(n).padStart(2, '0');
// const toDateInputValue = (date = new Date()) =>
//   `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
// import PatientSearchInput from './ui/PatientSearchInput';


const pad = (n) => String(n).padStart(2, '0');
const toDateInputValue = (date = new Date()) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const toTimeInputValue = (date = new Date()) =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

// Convert 24-hour time to 12-hour format with AM/PM
const to12HourFormat = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${pad(hours12)}:${pad(minutes)} ${period}`;
};

// Convert 12-hour time back to 24-hour format for storage
const from12HourFormat = (time12) => {
  if (!time12) return '';
  // Extract time and period (e.g., "02:30 PM" -> "14:30")
  const match = time12.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return time12;
  
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();
  
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  
  return `${pad(hours)}:${minutes}`;
};
const addMinutesToTime = (time, minutes) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const d = new Date(); d.setHours(h, m + minutes, 0, 0);
  return toTimeInputValue(d);
};

// Converts a time into minutes so leave and booking ranges can be compared safely.
const timeValueToMinutes = (value) => {
  if (!value) return null;
  const [hours, minutes] = String(value).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};
// ── Doctor leave-slot lookup (mirrors AvailabilityViewer.jsx) ──────────
// Maps the leave-calendar API's slot keys (e.g. "nine_00_am") to 15-min
// time ranges, so a selected start/end time can be checked against a
// doctor's marked-unavailable slots even when the checker grid isn't open.
const flattenLeaveSlotLookup = () => {
  const lookup = {};
  [
    ...DOCTOR_LEAVE_EARLY_TIMINGS,
    ...APPT_MORNING_TIMINGS,
    ...APPT_EVENING_TIMINGS,
  ].forEach((row) => {
    Object.entries(row).forEach(([key, value]) => {
      if (!key.startsWith('type') || !value) return;
      const timeKey = key.replace('type', 'time');
      const start = row[timeKey];
      if (start) {
        const [hour, minute] = String(start).split(':');
        const normalizedStart = `${String(Number(hour)).padStart(2, '0')}:${minute}`;
        lookup[value] = {
          start: normalizedStart,
          end: addMinutesToTime(normalizedStart, 15),
        };
      }
    });
  });
  return lookup;
};

const LEAVE_SLOT_LOOKUP = flattenLeaveSlotLookup();

// Converts the leave API response into time ranges used by booking validation.
const parseLeaveMapToSlots = (
  leaveMap = {},
  bookingStartTime,
  bookingEndTime,
  unavailableSlotValue
) => {
  const supportedSlotKeys = Object.keys(LEAVE_SLOT_LOOKUP);
  const isFullDayLeave =
    supportedSlotKeys.length > 0 &&
    supportedSlotKeys.every(
      (key) => leaveMap[key] === unavailableSlotValue,
    );

  // When every persisted slot is unavailable, the doctor selected Full-Day.
  if (isFullDayLeave) {
    return [{ start: bookingStartTime, end: bookingEndTime }];
  }

  const notAvailable = [];
  Object.entries(leaveMap || {}).forEach(([key, value]) => {
    if (value !== unavailableSlotValue) return;
    const slotInfo = LEAVE_SLOT_LOOKUP[key];
    if (slotInfo) {
      notAvailable.push(slotInfo);
      return;
    }
    if (typeof key === 'string' && key.includes(':')) {
      const start = key.slice(0, 5);
      notAvailable.push({ start, end: addMinutesToTime(start, 15) });
    }
  });
  return notAvailable;
};

const isTodayDate = (v) => v === toDateInputValue();
const getMinimumStartTime = (dateValue, bookingStartTime) => {
  if (!isTodayDate(dateValue)) return bookingStartTime;
  const now = toTimeInputValue();
  return now > bookingStartTime ? now : bookingStartTime;
};

const timeStringToDate = (timeValue) => {
  if (!timeValue) return null;
  const [hours, minutes] = String(timeValue).split(':').map((part) => Number(part));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return new Date(1970, 0, 1, hours, minutes, 0, 0);
};

const timeDateToString = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  return `${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

const asList = (v) => (Array.isArray(v) ? v : []);

// ── Entity ID — covers both snake_case and camelCase ──────
const getEntityId = (e) =>
  e?.id        ?? e?.patient_id  ?? e?.patientId  ??
  e?.doctor_id ?? e?.doctorId    ??
  e?.gvId     ?? e?.gvid         ??
  e?.room_id   ?? e?.roomId      ??
  e?.personId  ?? e?.code        ?? '';

const getDoctorId = (d) =>
  d?.gvId      ?? d?.gvid         ??
  d?.doctor_id ?? d?.doctorId    ??
  d?.personId  ?? d?.person_id   ??
  d?.id        ?? '';

// ── Label helpers ─────────────────────────────────────────
const getPatientLabel = (p) => {
  if (!p) return 'Patient';
  const name = p?.fname || p?.first_name || p?.name || p?.fullName ||
    p?.patient_name || p?.displayName || '';
  const code = p?.patientcode || p?.patient_code || p?.patientCode || p?.code || '';
  const phno = p?.phno || p?.phoneno || p?.phone || '';
  // Include patient code for searchability by ID
  const idInfo = code ? `[${code}]` : (p?.id ? `[ID:${p.id}]` : '');
  if (name && idInfo) return `${name} ${idInfo}`;
  if (name) return name;
  if (idInfo) return `Patient ${idInfo}`;
  return phno ? `Patient (${phno})` : 'Patient';
};

const getLatestVisitAssignment = (patient) => {
  const visitAssignmentList = [
    ...asList(patient?.visitAssignmentList),
    ...asList(patient?.visit?.visitAssignmentList),
  ].filter(Boolean);

  if (visitAssignmentList.length === 0) return null;

  const latestOpenAssignment = [...visitAssignmentList].reverse().find((assignment) => {
    const assignmentType = String(assignment?.type || '').toLowerCase();
    const assignmentStatus = String(assignment?.status || '').toLowerCase();
    return assignmentStatus === 'open' && (assignmentType === 'doctor' || assignmentType === 'counsellor' || assignmentType === 'counselor');
  });

  return latestOpenAssignment || visitAssignmentList[visitAssignmentList.length - 1];
};

const normalizePatientRecord = (patient) => {
  if (!patient) return null;

  const visit = patient.visit || {};
  const visitId = patient.visitId || visit.id || '';
  const fullName = patient.fullName || [patient.fname, patient.mname, patient.lname].filter(Boolean).join(' ').trim() || patient.patient_name || patient.first_name || patient.name || 'Patient';
  const phone = patient.phone || patient.phno || patient.phoneno || '';

  const selectedPatient = {
    ...patient,
    id: patient.id ?? patient.patientId ?? patient.patient_id ?? '',
    patientId: patient.patientId ?? patient.patient_id ?? patient.id ?? '',
    patient_name: patient.patient_name || fullName,
    fname: patient.fname || fullName,
    fullName,
    phone,
    phno: phone,
    visitId: String(visitId || ''),
    visit,
  };

  const latestAssignment = getLatestVisitAssignment(selectedPatient);
  if (latestAssignment?.type && !selectedPatient.assignedType) {
    selectedPatient.assignedType = latestAssignment.type;
  }
  if (latestAssignment?.targetid !== undefined && latestAssignment?.targetid !== null && selectedPatient.assignedDoctorId === undefined) {
    selectedPatient.assignedDoctorId = latestAssignment.targetid;
  }

  return selectedPatient;
};

const getDoctorLabel = (d) => {
  if (!d) return 'Doctor';
  if (d.label) return d.label;
  // gvlist entries from /api/v1/person have: id, type, identifier, role, identiifierlist
  const idVal = d.identifier || d.doctor_identifier || d.doctor_name || '';
  const typeVal = d.type || d.doctor_type || 'Doctor';
  const name = [d.first_name, d.mname, d.last_name, d.name, d.fullName]
    .filter(Boolean).join(' ').trim();
  const title = d.displaytitle || d.title || d.degree || '';
  const namedPart = [title, name].filter(Boolean).join(' ').trim() ||
         d.displayName || '';
  // Build label with identifier (phone/email) for searchability
  if (namedPart && idVal) return `${namedPart} - ${idVal}`;
  if (namedPart) return namedPart;
  if (idVal && typeVal) return `${idVal} (${typeVal})`;
  if (idVal) return idVal;
  if (typeVal) return `Doctor (${typeVal})`;
  return d.displayName || d.label || 'Doctor';
};

const getRoomLabel = (r) => {
  if (!r) return 'Room';
  const code = r.roomCode || r.room_code || r.code || '';
  const name = r.roomName || r.room_name || r.name || '';
  if (code && name) return `${code} - ${name}`;
  return name || code || r.label || 'Room';
};

const getPackageLabel = (p) =>
  p?.name || p?.packageName || p?.package_name || p?.title || p?.label || '';

const getServiceLabel = (s) =>
  s?.name || s?.serviceName || s?.service_name ||
  s?.package_service_name || s?.compositeService || s?.title || s?.label || 'Service';

const getServiceType = (s) =>
  s?.serviceType || s?.service_type || s?.packagetype || '';

const BOOKING_MODES = [
  { value: 'date',   label: 'By Date' },
  { value: 'doctor', label: 'By Doctor' },
  // { value: 'both',   label: 'Both (Date + Doctor)' },
];

const isDoctorBookingMode = (mode) => String(mode || 'doctor').toLowerCase() === 'doctor';

const getDefaultStartTime = () => {
  const now = new Date();
  // Add 1 minute to current time
  const nextMin = new Date(now.getTime() + 60000);
  const h = nextMin.getHours();
  const m = nextMin.getMinutes();
  // Round up to next 15 minutes
  const roundedMins = Math.ceil(m / 15) * 15;
  const adjDate = new Date(nextMin);
  adjDate.setMinutes(roundedMins);
  adjDate.setSeconds(0);
  if (adjDate.getMinutes() >= 60) {
    adjDate.setHours(adjDate.getHours() + 1);
    adjDate.setMinutes(0);
  }
  return `${pad(adjDate.getHours())}:${pad(adjDate.getMinutes())}`;
};

const getDefaultEndTime = (start, bookingEndTime) => {
  if (!start) return '';
  const [h, m] = start.split(':').map(Number);
  // Add 15 minutes
  const totalMins = h * 60 + m + 15;
  const endH = Math.floor(totalMins / 60);
  const endM = totalMins % 60;
  const result = `${pad(endH)}:${pad(endM)}`;
  return result <= bookingEndTime ? result : '';
};

const createEmptyForm = (bookingEndTime) => {
  const start = getDefaultStartTime();
  const end   = getDefaultEndTime(start, bookingEndTime);
  return {
    booking_mode:       'doctor',
    patient_id:         '',
    package_id:         '',
    package_service_id: '',
    session_fraction:   '1/1',
    start_date:         toDateInputValue(),
    doctor_id:          '',
    room_id:            '',
    start_time:         start,
    end_time:           end && end <= bookingEndTime ? end : '',
    medicine_oil:       '',
    notes:              '',
    type:               'Doctor',
    counselor:          '',
  };
};

const parseSessionFraction = (fraction = '1/1') => {
  const [session_number, total_sessions] = String(fraction || '1/1').split('/').map((value) => parseInt(value, 10));
  return [
    Number.isFinite(session_number) && !Number.isNaN(session_number) ? session_number : 1,
    Number.isFinite(total_sessions) && !Number.isNaN(total_sessions) ? total_sessions : 1,
  ];
};

const buildBookingPlanPayload = ({
  formData,
  selectedProduct,
  packageSearchText = '',
  packageSearchText2 = '',
  doctorsList = [],
  existingVisitId = null,
  urlVisitId = null,
  doctorDepartmentId = null,
  locationState = null,
  selectedPatient = null,
  isAssignDoctorMode = false,
  treatment2 = null,
}) => {
  const [session_number, total_sessions] = parseSessionFraction(formData.session_fraction || '1/1');

  // ── Second "Doctor and treatment information (2)" card (optional) ────────
  let doctorId2 = null;
  let packageId2 = null;
  let packageServiceId2 = null;
  let medicineOil2 = '';
  let notes2 = '';
  let totalSessions2 = null;
  let sessionNumber2 = null;
  if (treatment2) {
    const rawPkgId2 = treatment2.package_id;
    packageId2 = rawPkgId2 && rawPkgId2 !== '' && rawPkgId2 !== '0' && rawPkgId2 !== 0
      ? Number(rawPkgId2) : null;
    const rawPkgSvcId2 = treatment2.package_service_id;
    packageServiceId2 = rawPkgSvcId2 && rawPkgSvcId2 !== '' && rawPkgSvcId2 !== '0' && rawPkgSvcId2 !== 0
      ? Number(rawPkgSvcId2) : null;

    const doctorValue2 = treatment2.doctor_id || '';
    if (doctorValue2) {
      const selectedDoctor2 = doctorsList.find((d) => {
        if (!d) return false;
        const doctorId = String(resolveDoctorGvId(d) || getDoctorId(d));
        return doctorId === String(doctorValue2);
      }) || null;
      const rawDoctorId2 = selectedDoctor2
        ? resolveDoctorGvId(selectedDoctor2) || getDoctorId(selectedDoctor2)
        : doctorValue2;
      doctorId2 = rawDoctorId2 !== undefined && rawDoctorId2 !== null && !Number.isNaN(Number(rawDoctorId2))
        ? Number(rawDoctorId2)
        : null;
    }

    medicineOil2 = (treatment2.medicine_oil || '').trim();
    notes2 = (treatment2.notes || '').trim();
    const [parsedSessionNumber2, parsedTotalSessions2] = parseSessionFraction(treatment2.session_fraction || '1/1');
    sessionNumber2 = parsedSessionNumber2;
    totalSessions2 = parsedTotalSessions2;
  }

  const rawPkgId = formData.package_id;
  const pkgId = rawPkgId && rawPkgId !== '' && rawPkgId !== '0' && rawPkgId !== 0
    ? Number(rawPkgId) : null;
  const rawPkgSvcId = formData.package_service_id;
  const pkgSvcId = rawPkgSvcId && rawPkgSvcId !== '' && rawPkgSvcId !== '0' && rawPkgSvcId !== 0
    ? Number(rawPkgSvcId) : null;

  const doctorValue = formData.doctor_id || '';
  const selectedDoctor = doctorsList.find((d) => {
    if (!d) return false;
    const doctorId = String(resolveDoctorGvId(d) || getDoctorId(d));
    return doctorId === String(doctorValue);
  }) || null;

  const rawDoctorId = selectedDoctor
    ? resolveDoctorGvId(selectedDoctor) || getDoctorId(selectedDoctor)
    : doctorValue;
  const gvId = rawDoctorId !== undefined && rawDoctorId !== null && !Number.isNaN(Number(rawDoctorId))
    ? Number(rawDoctorId)
    : null;

  const selectedDoctorType = String(
    selectedDoctor?.type || selectedDoctor?.doctor_type || selectedDoctor?.role || formData.type || ''
  ).trim();
  const isCounsellor = selectedDoctorType.toLowerCase() === 'counsellor';
  const appointmentType = isCounsellor ? 'Counsellor' : (formData.type || 'Doctor');
  const patientId = formData.patient_id || selectedPatient?.id || selectedPatient?.patientId || null;
  const visitId = existingVisitId ? Number(existingVisitId) : (urlVisitId ? Number(urlVisitId) : null);

  // Package search also accepts free text. Persist exactly what the user typed
  // as the treatment name; when the field is empty, store General Treatment.
  const typedTreatmentName = String(packageSearchText || '').trim();
  const treatmentName = typedTreatmentName || String(formData.treatment_name || '').trim() || 'General Treatment';
  const treatmentName2 = String(packageSearchText2 || treatment2?.treatment_name || '').trim();

  const visitObj = {
    id: visitId || null,
    patientId: patientId ? Number(patientId) : null,
    /*apptdate: formData.start_date || null,
    appthours: formData.start_time ? String(formData.start_time).split(':')[0] : null,
    apptminutes: formData.start_time ? String(formData.start_time).split(':')[1] : null,*/
    apptdate: gvId ? (formData.start_date || null) : null,
    appthours: gvId && formData.start_time ? String(formData.start_time).split(':')[0] : null,
    apptminutes: gvId && formData.start_time ? String(formData.start_time).split(':')[1] : null,
    type: appointmentType,
    departmentId: doctorDepartmentId || selectedDoctor?.departmentId || selectedDoctor?.department_id || 0,
    // VisitDao.updateVisit updates this column as part of the same statement.
    // Always pass the current value so a doctor/booking edit cannot null it.
    internalStatus:
      selectedPatient?.internalStatus ||
      selectedPatient?.visit?.internalStatus ||
      formData?.internalStatus ||
      formData?.visit?.internalStatus ||
      null,
  };

  // When creating or editing a booking session (NOT assign-doctor mode),
  // save the groupname as "new session" (lowercase, matches the literal the
  // Booking Plans list filters on) to distinguish these records in the
  // visitassignment table from "Primary Doctor" assignments made through
  // the SearchDocList assign-doctor flow.
  // In assign-doctor mode, groupname is null so the backend preserves
  // the existing "Primary Doctor" assignment logic (booking_plans.groupname
  // itself is separately tagged "assigndoctor" by the backend in this case).
  const sessionGroupname = isAssignDoctorMode
    ? (locationState?.groupname || null)
    : BOOKING_PLAN_GROUP_NEW_SESSION;

  const visitAssignmentObj = gvId ? {
    visitid: visitId || null,
    targetid: gvId,
    type: appointmentType,
    status: 'open',
    groupname: sessionGroupname,
  } : null;

  return {
    patient_id:         patientId ? Number(patientId) : null,
    patientId:          patientId ? Number(patientId) : null,
    visit_id:           visitId || null,
    visitId:            visitId || null,
    package_id:         pkgId,
    packageId:          pkgId,
    package_service_id: pkgSvcId,
    packageServiceId:   pkgSvcId,
    product_id:         selectedProduct?.productId || formData.product_id || null,
    product_name:       selectedProduct?.name || selectedProduct?.productName || formData.product_name || '',
    category_name:      selectedProduct?.categoryName || selectedProduct?.productType || formData.category_name || '',
    session_number,
    total_sessions,
    total_days:         formData.total_days != null && formData.total_days !== '' ? Number(formData.total_days) : null,
    doctor_id:          gvId,
    counselor:          isCounsellor ? gvId : null,
    type:               appointmentType,
    start_date:         formData.start_date || null,
    start_time:         formData.start_time || null,
    end_time:           formData.end_time || null,
    room_id:            formData.room_id ? Number(formData.room_id) : null,
    medicine_oil:       (formData.medicine_oil || '').trim(),
    notes:              formData.notes?.trim() || '',
    booking_mode:       formData.booking_mode || 'doctor',
    treatment_name:     treatmentName,
    doctor_id2:          doctorId2,
    package_id2:         packageId2,
    package_service_id2: packageServiceId2,
    medicine_oil2:       medicineOil2,
    notes2:              notes2,
    total_sessions2:     totalSessions2,
    session_number2:     sessionNumber2,
    treatment_name2:     treatmentName2,
    departmentId:       doctorDepartmentId || selectedDoctor?.departmentId || selectedDoctor?.department_id || 0,
    department_id:      doctorDepartmentId || selectedDoctor?.departmentId || selectedDoctor?.department_id || 0,
    groupname:          sessionGroupname,  // null in assign-doctor mode, 'new session' otherwise
    // Pass visitassignmentid from the existing booking plan to skip room validation when editing
    visitassignmentid:  formData.visitassignmentid || null,
    v: visitObj,
    va: visitAssignmentObj,
    gvid: gvId,
    /*apptdate: formData.start_date || null,
    appthours: formData.start_time ? String(formData.start_time).split(':')[0] : null,
    apptminutes: formData.start_time ? String(formData.start_time).split(':')[1] : null,*/
    apptdate: gvId ? (formData.start_date || null) : null,
    appthours: gvId && formData.start_time ? String(formData.start_time).split(':')[0] : null,
    apptminutes: gvId && formData.start_time ? String(formData.start_time).split(':')[1] : null, 
  };
};

// ── 12-Hour Time Picker Component ────────────────────────
const TimePicker12Hour = (props) => {
  const {
    value,
    onChange,
    error,
    disabled,
    bookingStartTime,
    bookingEndTime,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState('12');
  const [minutes, setMinutes] = useState('00');
  const [period, setPeriod] = useState('AM');
  const containerRef = useRef(null);

  // Generate available hours based on booking window (6:00 AM - 8:00 PM)
  const getAvailableHours = () => {
    const hours = [];
    // 6 AM to 12 PM (6 to 12)
    for (let h = 6; h <= 12; h++) {
      hours.push({ value: String(h), label: String(h) });
    }
    // 1 PM to 8 PM (13 to 20 in 24h format, displayed as 1 to 8 in 12h format)
    for (let h = 1; h <= 8; h++) {
      hours.push({ value: String(h), label: String(h) });
    }
    return hours;
  };

  const availableHours = getAvailableHours();

  // Convert 24h value to 12h components
  useEffect(() => {
    if (!value) return;
    const [h, m] = value.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    
    const periodVal = h >= 12 ? 'PM' : 'AM';
    const hours12 = h % 12 || 12;
    
    setHours(String(hours12));
    setMinutes(String(m).padStart(2, '0'));
    setPeriod(periodVal);
  }, [value]);

  // const handleChange = () => {
  //   // Convert 12h to 24h format
  //   let h24 = parseInt(hours, 10);
  //   if (period === 'PM' && h24 !== 12) h24 += 12;
  //   if (period === 'AM' && h24 === 12) h24 = 0;
    
  //   const time24 = `${String(h24).padStart(2, '0')}:${minutes}`;
  //   onChange(time24);
  // };
// NEW — add this instead
const emit = (newHours, newMinutes, newPeriod, fieldName = 'start_time') => {
  let h24 = parseInt(newHours, 10);
  if (newPeriod === 'PM' && h24 !== 12) h24 += 12;
  if (newPeriod === 'AM' && h24 === 12) h24 = 0;
  const time24 = `${String(h24).padStart(2, '0')}:${newMinutes}`;
  
  // Validate time is within allowed range (6:00 AM - 8:00 PM)
  if (time24 < bookingStartTime || time24 > bookingEndTime) {
    const errorMsg = time24 < bookingStartTime
      ? 'Earliest booking time is 12:00 AM'
      : 'Latest booking time is 12:59 PM';
    console.error('[TimePicker] Invalid time selected:', time24, '-', errorMsg);
    
    // Show immediate validation error by updating the parent's error state
    // We need to call the parent's validation or set error directly
    // For now, emit the value and let the parent validation handle it
    // The parent component's validate() function will catch this
  }
  
  onChange(time24);
};
  const isDisabled = Boolean(disabled);
  const hasError = Boolean(error);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const displayValue = `${hours}:${minutes} ${period}`;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Single Input Box */}
      <div
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minWidth: 120,
          padding: '8px 12px',
          backgroundColor: '#fff',
          border: `1.5px solid ${hasError ? '#dc2626' : '#e2e8f0'}`,
          borderRadius: 10,
          boxShadow: hasError ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          userSelect: 'none',
        }}
      >
        <span style={{
          fontSize: 14,
          fontWeight: 500,
          color: '#1e293b',
          fontFamily: 'monospace',
        }}>
          {displayValue}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#64748b' }}>
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      </div>

      {/* Dropdown Picker */}
      {isOpen && !isDisabled && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
          padding: 16,
          display: 'flex',
          gap: 12,
          alignItems: 'center',
        }}>
          {/* Hours */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Hour</label>
            <select
              value={hours}
              onChange={(e) => { setHours(e.target.value);emit(e.target.value, minutes, period);  }}
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                background: '#fff',
                color: '#1e293b',
                cursor: 'pointer',
                minWidth: 60,
                outline: 'none',
              }}
            >
              {availableHours.map((h) => (
                <option key={h.value} value={h.value}>{h.label}</option>
              ))}
            </select>
          </div>

          {/* Separator */}
          <span style={{ 
            color: '#94a3b8', 
            fontWeight: 700, 
            fontSize: 18,
            paddingTop: 20,
            userSelect: 'none'
          }}>:</span>

          {/* Minutes */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Min</label>
            <select
              value={minutes}
              onChange={(e) => { setMinutes(e.target.value);  emit(hours, e.target.value, period); }}
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                background: '#fff',
                color: '#1e293b',
                cursor: 'pointer',
                minWidth: 60,
                outline: 'none',
              }}
            >
              {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                <option key={m} value={String(m).padStart(2, '0')}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
          </div>

          {/* AM/PM Toggle */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            paddingTop: 16,
          }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Period</label>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '2px',
              backgroundColor: '#f1f5f9',
              borderRadius: 6,
            }}>
              <button
                type="button"
                onClick={() => { setPeriod('AM');  emit(hours, minutes, 'AM'); }}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: period === 'AM' ? '#3b82f6' : 'transparent',
                  color: period === 'AM' ? '#fff' : '#64748b',
                  transition: 'all 0.2s ease',
                  lineHeight: 1,
                  minWidth: 40,
                }}
              >
                AM
              </button>
              <button
                type="button"
                onClick={() => { setPeriod('PM'); emit(hours, minutes, 'PM'); }}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: period === 'PM' ? '#3b82f6' : 'transparent',
                  color: period === 'PM' ? '#fff' : '#64748b',
                  transition: 'all 0.2s ease',
                  lineHeight: 1,
                  minWidth: 40,
                }}
              >
                PM
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Searchable Select ─────────────────────────────────────
const SearchableSelect = ({ options = [], value, onChange, placeholder, error, label, disabled }) => {
  const [search, setSearch] = useState('');
  const [open,   setOpen]   = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const safe     = Array.isArray(options) ? options : [];
  const selLabel = safe.find((o) => String(o.value) === String(value))?.label || '';
  const filtered = safe.filter((o) =>
    String(o.label || '').toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="form-group searchable-select">
      {label && <label className="form-label">{label}</label>}
      <div className="searchable-select-input">
        <input
          className={`form-control${error ? ' is-invalid' : ''}`}
          value={open ? search : selLabel}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => { if (!disabled) { setOpen(true); setSearch(''); } }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
        />
        {open && !disabled && (
          <div className="search-dropdown">
            {filtered.length === 0
              ? <div className="dropdown-empty">No results found</div>
              : Array.from(new Map(filtered.map(o => [String(o.value), o])).values()).map((o, idx) => (
                  <button type="button" key={`${String(o.value)}-${idx}`} className="dropdown-item"
                    onMouseDown={() => { onChange(o.value); setOpen(false); setSearch(''); }}>
                    {o.label}
                  </button>
                ))
            }
          </div>
        )}
      </div>
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

// ── Doctor & Treatment Card (reusable — supports a 2nd card) ──
const iconButtonStyle = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '1.5px solid #2563eb',
  background: '#eff6ff',
  color: '#2563eb',
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
  boxShadow: '0 1px 2px rgba(37, 99, 235, 0.15)',
  transition: 'background-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease',
};

const handleIconBtnEnter = (e) => {
  e.currentTarget.style.background = '#dbeafe';
  e.currentTarget.style.boxShadow = '0 2px 5px rgba(37, 99, 235, 0.3)';
  e.currentTarget.style.transform = 'translateY(-1px)';
};

const handleIconBtnLeave = (e) => {
  e.currentTarget.style.background = iconButtonStyle.background;
  e.currentTarget.style.boxShadow = iconButtonStyle.boxShadow;
  e.currentTarget.style.transform = 'none';
};

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconMinus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TreatmentInfoCard = ({
  title,
  doctors,
  doctorId,
  onDoctorChange,
  doctorError,
  doctorOptional,
  showViewSchedule,
  onViewSchedule,
  packageSearch,
  onPackageSearchChange,
  packageOpen,
  setPackageOpen,
  packageResults,
  searchingPackage,
  onSelectPackage,
  sessionFraction,
  onSessionFractionChange,
  sessionFractionError,
  sessionFractionOptional,
  medicineOil,
  onMedicineOilChange,
  medicineOilError,
  notes,
  onNotesChange,
  showOptionalFields,
  headerAction,
}) => (
  <div className="form-section compact-section">
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <h3 className="form-section-title compact-title" style={{ margin: 0 }}>{title}</h3>
      {headerAction}
    </div>

    <div className="form-row-2col" style={{ marginTop: 0, marginBottom: showOptionalFields ? 12 : 0 }}>
      <div className="form-group compact-group">
        <SearchableSelect
          label={
            <>
              Doctor/Therapist{doctorOptional ? ' (Optional)' : ''}
              {doctorId && showViewSchedule && (
                <span style={{ fontSize: 11, color: '#2563eb', marginLeft: 8, cursor: 'pointer', fontWeight: 500 }}
                  onClick={onViewSchedule}>
                  View schedule →
                </span>
              )}
            </>
          }
          options={[
            { value: '', label: 'Select Doctor' },
            ...doctors.map((d) => ({
              value: String(resolveDoctorGvId(d) || getDoctorId(d)),
              label: getDoctorLabel(d),
            })),
          ]}
          value={String(doctorId)}
          onChange={onDoctorChange}
          error={doctorError}
          placeholder="Search doctor..."
        />
      </div>

      {showOptionalFields && (
        <div className="form-group compact-group">
          <label className="form-label ">
            Package (Search Package + Service)
          </label>

          <div style={{ position: 'relative' }}>
            <input
              type="text"
              className="form-control"
              value={packageSearch}
              placeholder="Type at least 3 characters..."
              onChange={(e) => {
                onPackageSearchChange(e.target.value);
                setPackageOpen(true);
              }}
              onFocus={() => setPackageOpen(true)}
              onBlur={() => setTimeout(() => setPackageOpen(false), 200)}
              autoComplete="off"
            />
            {searchingPackage && (
              <span style={{ position: 'absolute', right: 10, top: 8, fontSize: 12, color: '#64748b' }}>
                Searching...
              </span>
            )}
            {packageOpen && packageResults.length > 0 && (
              <div className="search-dropdown" style={{
                position: 'absolute',
                width: '100%',
                zIndex: 999,
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                maxHeight: 220,
                overflowY: 'auto'
              }}>
                {packageResults.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className="dropdown-item"
                    onMouseDown={() => onSelectPackage(item)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px',
                      border: 'none',
                      background: 'white',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
            {/* The empty-result message belongs to the package dropdown. Checking
                packageOpen prevents a stale "No matching packages" message from
                remaining visible after the user closes the dropdown. */}
            {packageOpen && packageSearch.trim().length >= 3 && !searchingPackage && packageResults.length === 0 && (
              <div style={{
                position: 'absolute', width: '100%', zIndex: 999,
                padding: '12px', background: '#fff',
                border: '1px solid #e2e8f0', borderRadius: 8,
                textAlign: 'center', fontSize: 13, color: '#94a3b8',
              }}>
                No matching packages found
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {showOptionalFields && (
      <>
        <div className="form-row-2col" style={{ marginTop: 0, marginBottom: 12 }}>
          <div className="form-group compact-group">
            <label className={`form-label${sessionFractionOptional ? '' : ' form-required'}`}>
              Session Fraction{sessionFractionOptional ? ' (Optional)' : ''}
            </label>
            <input type="text"
              className={`form-control${sessionFractionError ? ' is-invalid' : ''}`}
              value={sessionFraction}
              onChange={(e) => onSessionFractionChange(e.target.value)}
              placeholder="e.g. 1/3"
            />
            {sessionFractionError && <div className="invalid-feedback">{sessionFractionError}</div>}
          </div>
          <div className="form-group compact-group">
            <label className="form-label form-required">Medicine / Oil</label>
            <input type="text"
              className={`form-control${medicineOilError ? ' is-invalid' : ''}`}
              value={medicineOil}
              onChange={(e) => {
                if (e.target.value.length <= 255) {
                  onMedicineOilChange(e.target.value);
                }
              }}
              placeholder="Enter medicine or oil..."
              maxLength={255}
            />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, textAlign: 'right' }}>
              {medicineOil?.length || 0}/255
            </div>
            {medicineOilError && <div className="invalid-feedback">{medicineOilError}</div>}
          </div>
        </div>

        <div className="form-group compact-group">
          <label className="form-label">Notes</label>
          <textarea className="form-control" value={notes}
            onChange={(e) => {
              if (e.target.value.length <= 255) {
                onNotesChange(e.target.value);
              }
            }}
            rows="3" placeholder="Additional notes..."
            maxLength={255} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, textAlign: 'right' }}>
            {notes?.length || 0}/255
          </div>
        </div>
      </>
    )}
  </div>
);

// ── Main Component ────────────────────────────────────────
export default function BookingNewSession() {
  const { id: urlId, patientId: urlPatientId, visitId: urlVisitId } = useParams();
  const history  = useHistory();
  const routerLocation = useLocation();
  const dispatch = useDispatch();
  const org = useOrganization();
  const bookingStartTime = getOrgConstant(
    Appointmant_schedule_session_booking_start_time,
    org?.orgCode
  );
  const bookingEndTime = getOrgConstant(
    Appointmant_schedule_session_booking_end_time,
    org?.orgCode
  );
  const doctorLeaveUnavailableSlotValue = getOrgConstant(
    Docter_leave_unavailable_slot_value,
    org?.orgCode
  );
  const editPlan = routerLocation.state?.plan || (urlId ? { id: urlId } : null);
  const isEdit   = Boolean(editPlan?.id);
  const [fetchingPlan, setFetchingPlan] = useState(false);
  

  // ── If URL has an :id but no plan was passed via state, fetch it ──
  // Also handles navigation from PatientSections where plan in state has only
  // partial fields (constructed from room data) — fetches the full booking plan from API.
  useEffect(() => {
    // Determine the booking ID to fetch: prefer urlId, fall back to plan.id from state
    const bookingIdToFetch = urlId || routerLocation.state?.plan?.id || null;
    if (!bookingIdToFetch) return;
    // If urlId exists and we already have a full plan in state (not manually constructed),
    // skip fetching. We detect "manual" plans by checking if they're missing key fields
    // that the API always returns (like room_id or doctor_id).
    if (urlId && routerLocation.state?.plan?.room_id != null) return;
    // If no urlId but plan exists in state (from PatientSections manual construction),
    // always fetch to get the complete data
    let cancelled = false;
    const doFetch = async () => {
      setFetchingPlan(true);
      try {
        const response = await bookingplanService.getBookingPlanById(bookingIdToFetch);
        const plan = response?.data?.data || response?.data || response;
        if (plan && typeof plan === 'object' && !cancelled) {
          // Always replace in-place on the current route to avoid unmounting/
          // remounting the component (which loses all local state). 
          // Previously the code navigated to a hardcoded /booking/new-session/{id}
          // path, but when coming from PatientSections this changed the route
          // and caused the component to remount, losing the form data.
          history.replace(routerLocation.pathname, { plan, ...routerLocation.state });
        }
      } catch (err) {
        console.error('[BookingNewSession] Failed to fetch plan by URL id:', err);
        toast.error('Failed to load booking plan');
      } finally {
        if (!cancelled) setFetchingPlan(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [urlId, routerLocation.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── If URL does not have :id but has urlPatientId and no plan is in state, fetch the patient's existing active booking plan ──
  useEffect(() => {
    if (
      urlId ||
      !urlPatientId ||
      routerLocation.state?.plan ||
      routerLocation.state?.mode !== 'EDIT_DOCTOR'
    ) return;
    let cancelled = false;
    const fetchExistingPlan = async () => {
      setFetchingPlan(true);
      try {
        const response = await bookingplanService.getBookingPlanByPatientId(urlPatientId, true);
        const plan = response?.data?.data || response?.data || response;
        const planVisitId = plan?.visit_id ?? plan?.visitId;
        const belongsToCurrentVisit =
          !urlVisitId || String(planVisitId) === String(urlVisitId);

        if (plan && typeof plan === 'object' && belongsToCurrentVisit && !cancelled) {
          console.log('[BookingNewSession] Loaded existing patient booking plan:', plan);
          history.replace({
            pathname: routerLocation.pathname,
            state: {
              ...routerLocation.state,
              plan: plan
            }
          });
        } else if (plan && !belongsToCurrentVisit) {
          console.log(
            '[BookingNewSession] Ignoring booking plan from another visit:',
            planVisitId,
            'current visit:',
            urlVisitId
          );
        }
      } catch (err) {
        console.error('[BookingNewSession] Failed to fetch existing booking plan by patient id:', err);
      } finally {
        if (!cancelled) setFetchingPlan(false);
      }
    };
    fetchExistingPlan();
    return () => { cancelled = true; };
  }, [urlPatientId, urlVisitId, urlId, routerLocation.state?.plan, routerLocation.state?.mode]);

  const [form,             setForm]             = useState(() => createEmptyForm(bookingEndTime));
  const [saving,           setSaving]           = useState(false);
  const [errors,           setErrors]           = useState({});
  const [patients,         setPatients]         = useState([]);
  const [doctors,          setDoctors]          = useState([]);
  const [rooms,            setRooms]            = useState([]);
  const [packages,         setPackages]         = useState([]);
  const [combinedPackageServices, setCombinedPackageServices] = useState([]);
  const [selectedProduct,  setSelectedProduct]  = useState(null);
  const [productSearch,    setProductSearch]    = useState('');
  const [productResults,   setProductResults]   = useState([]);
  const [searchingProduct, setSearchingProduct] = useState(false);
  const [productSearchOpen,setProductSearchOpen]= useState(false);
  const productSearchRef = useRef(null);
  const debounceTimer = useRef(null);
  const [activePanel,      setActivePanel]      = useState(null);
  const [checkerDoctorSlot, setCheckerDoctorSlot] = useState('primary');
  const [conflictMessage,  setConflictMessage]  = useState('');
  const [showPatientForm,  setShowPatientForm]  = useState(false);
  const [showErrorModal,   setShowErrorModal]   = useState(false);
  const [errorModalMessage,setErrorModalMessage]= useState('');
  const [doctorSchedule,   setDoctorSchedule]   = useState([]);
  const [doctorSchedule2,  setDoctorSchedule2]  = useState([]);
  const [doctorLeaveSlots,  setDoctorLeaveSlots]  = useState([]);
  const [doctorLeaveSlots2, setDoctorLeaveSlots2] = useState([]);
  const [roomOccupancy,    setRoomOccupancy]    = useState({});
  const [loadingSchedule,  setLoadingSchedule]  = useState(false);
  const [loadingSchedule2, setLoadingSchedule2] = useState(false);
  const [loadingData,      setLoadingData]      = useState(true);

  // Reloads the latest leave before submit so a stale screen cannot allow a booking.
  const fetchLatestDoctorLeaveSlots = useCallback((doctorId, date) =>
    new Promise((resolve, reject) => {
      if (!doctorId || !date) {
        resolve([]);
        return;
      }

      dispatch(patientActions.getleaveCalender(doctorId, date, (response) => {
        if (typeof response === 'function' || typeof response === 'string') {
          reject(new Error('Doctor availability could not be loaded.'));
          return;
        }
        const leaveMap = response?.lc || response?.data?.lc || response?.data || {};
        resolve(parseLeaveMapToSlots(
          leaveMap,
          bookingStartTime,
          bookingEndTime,
          doctorLeaveUnavailableSlotValue
        ));
      }));
    }), [
      dispatch,
      bookingStartTime,
      bookingEndTime,
      doctorLeaveUnavailableSlotValue,
    ]);

  const [packageSearch, setPackageSearch] = useState('');
  const [packageOpen, setPackageOpen] = useState(false);
  const [packageResults, setPackageResults] = useState([]);
  const [searchingPackage, setSearchingPackage] = useState(false);
  const packageDebounceTimer = useRef(null);

  // ── Second "Doctor and treatment information" card (UI only — up to 2 total) ──
  const emptyTreatment2 = { doctor_id: '', package_id: '', package_service_id: '', treatment_name: '', session_fraction: '1/1', medicine_oil: '', notes: '' };
  const [showTreatment2, setShowTreatment2] = useState(false);
  const [treatment2, setTreatment2] = useState(emptyTreatment2);
  const setTreatment2Field = (k, v) => setTreatment2((prev) => ({ ...prev, [k]: v }));
  const [packageSearch2, setPackageSearch2] = useState('');
  const [packageOpen2, setPackageOpen2] = useState(false);
  const [packageResults2, setPackageResults2] = useState([]);
  const [searchingPackage2, setSearchingPackage2] = useState(false);
  const packageDebounceTimer2 = useRef(null);

  // Detect if we're in "assign doctor" or "edit doctor" mode (triggered from SearchDocList
  // via the Assign Doctor button with mode=ASSIGN_DOCTOR or Edit button with mode=EDIT_DOCTOR).
  // Both modes use the same simple form (doctor selection only, no package/room/treatment).
  // In both modes, groupname is null so the backend closes previous open visitassignment records.
  const isAssignDoctorMode = routerLocation.state?.mode === 'ASSIGN_DOCTOR' || routerLocation.state?.mode === 'EDIT_DOCTOR';
  
  // Detect if we're navigating from the Booking page (to show + button)
  const isFromBookingPage = routerLocation.state?.from === 'booking';

  // Package service search — local-first, API fallback
  //
  useEffect(() => {
    const q = packageSearch.trim().toLowerCase();

    if (q.length < 2) {
      setPackageResults([]);
      return;
    }

    // ── INSTANT local filter on already-loaded combinedPackageServices ──
    const localMatches = combinedPackageServices.filter((item) =>
      (item.label || '').toLowerCase().includes(q) ||
      (item.pkgName || '').toLowerCase().includes(q) ||
      (item.svcName || '').toLowerCase().includes(q)
    );
    setPackageResults(localMatches);
    setSearchingPackage(false);

    // ── API fallback (only when local results look thin AND query >= 3 chars) ──
    if (localMatches.length < 10 && q.length >= 3) {
      if (packageDebounceTimer.current) clearTimeout(packageDebounceTimer.current);
      packageDebounceTimer.current = setTimeout(async () => {
        setSearchingPackage(true);
        try {
          const resp = await bookingPlanService.searchPackageServices(q);
          const allSvcList = Array.isArray(resp.data) ? resp.data : [];

          if (allSvcList.length > 0) {
            // Build combined labels (same pattern as fetchDropdowns)
            const apiResults = [];
            const seen = new Set(localMatches.map((r) => r.value));
            allSvcList.forEach((svc) => {
              const svcId   = String(svc.id ?? '');
              const pkgId   = String(svc.packageid ?? svc.packageId ?? '');
              const pkgName = svc.packagename || svc.packageName || '';
              const svcName = getServiceLabel(svc);
              const svcType = getServiceType(svc);
              const key = `${pkgId}_${svcId}`;
              if (!seen.has(key)) {
                seen.add(key);
                apiResults.push({
                  value: key,
                  label: svcType ? `${pkgName} - ${svcName} (${svcType})` : `${pkgName} - ${svcName}`,
                  package_id: pkgId,
                  package_service_id: svcId,
                  pkgName,
                  svcName,
                });
              }
            });
            setPackageResults([...localMatches, ...apiResults]);
          }
        } catch (err) {
          console.error('[BookingNewSession] Package search API fallback failed:', err);
          // Keep showing local results on API error
        } finally {
          setSearchingPackage(false);
        }
      }, 400);
    }

    return () => {
      if (packageDebounceTimer.current) clearTimeout(packageDebounceTimer.current);
    };
  }, [packageSearch, combinedPackageServices]);

  // Package service search for the 2nd treatment card — same local-first, API fallback pattern
  useEffect(() => {
    const q = packageSearch2.trim().toLowerCase();

    if (q.length < 2) {
      setPackageResults2([]);
      return;
    }

    const localMatches = combinedPackageServices.filter((item) =>
      (item.label || '').toLowerCase().includes(q) ||
      (item.pkgName || '').toLowerCase().includes(q) ||
      (item.svcName || '').toLowerCase().includes(q)
    );
    setPackageResults2(localMatches);
    setSearchingPackage2(false);

    if (localMatches.length < 10 && q.length >= 3) {
      if (packageDebounceTimer2.current) clearTimeout(packageDebounceTimer2.current);
      packageDebounceTimer2.current = setTimeout(async () => {
        setSearchingPackage2(true);
        try {
          const resp = await bookingPlanService.searchPackageServices(q);
          const allSvcList = Array.isArray(resp.data) ? resp.data : [];

          if (allSvcList.length > 0) {
            const apiResults = [];
            const seen = new Set(localMatches.map((r) => r.value));
            allSvcList.forEach((svc) => {
              const svcId   = String(svc.id ?? '');
              const pkgId   = String(svc.packageid ?? svc.packageId ?? '');
              const pkgName = svc.packagename || svc.packageName || '';
              const svcName = getServiceLabel(svc);
              const svcType = getServiceType(svc);
              const key = `${pkgId}_${svcId}`;
              if (!seen.has(key)) {
                seen.add(key);
                apiResults.push({
                  value: key,
                  label: svcType ? `${pkgName} - ${svcName} (${svcType})` : `${pkgName} - ${svcName}`,
                  package_id: pkgId,
                  package_service_id: svcId,
                  pkgName,
                  svcName,
                });
              }
            });
            setPackageResults2([...localMatches, ...apiResults]);
          }
        } catch (err) {
          console.error('[BookingNewSession] Package search API fallback (card 2) failed:', err);
        } finally {
          setSearchingPackage2(false);
        }
      }, 400);
    }

    return () => {
      if (packageDebounceTimer2.current) clearTimeout(packageDebounceTimer2.current);
    };
  }, [packageSearch2, combinedPackageServices]);

  // ── Validation ────────────────────────────────────────
  const validate = useCallback((f) => {
    const e = {};
    const requiresDoctor = isDoctorBookingMode(f.booking_mode);
    if (!f.patient_id)   e.patient_id   = 'Select a patient';
    if (!isAssignDoctorMode && !f.booking_mode) e.booking_mode = 'Select booking mode';
    if (requiresDoctor && !f.doctor_id) e.doctor_id = 'Select a doctor';
    
    // Skip session_fraction and booking_mode validation in assign doctor mode
    if (!isAssignDoctorMode) {
      const parts = (f.session_fraction || '').split('/');
      if (parts.length !== 2) {
        e.session_fraction = 'Format: n/m';
      } else {
        const n = parseInt(parts[0], 10), m = parseInt(parts[1], 10);
        if (isNaN(n) || isNaN(m) || n <= 0 || m <= 0) e.session_fraction = 'Use positive numbers';
        else if (n > m) e.session_fraction = 'Current session cannot exceed total';
      }

      // Same doctor/session-fraction validation as the first card, applied to the
      // second treatment card when shown via the "+" button.
      if (showTreatment2) {
        if (requiresDoctor && !treatment2.doctor_id) e.treatment2_doctor_id = 'Select a doctor';

        if (treatment2.session_fraction) {
          const parts2 = treatment2.session_fraction.split('/');
          if (parts2.length !== 2) {
            e.treatment2_session_fraction = 'Format: n/m';
          } else {
            const n2 = parseInt(parts2[0], 10), m2 = parseInt(parts2[1], 10);
            if (isNaN(n2) || isNaN(m2) || n2 <= 0 || m2 <= 0) e.treatment2_session_fraction = 'Use positive numbers';
            else if (n2 > m2) e.treatment2_session_fraction = 'Current session cannot exceed total';
          }
        }
      }
    }
    if (!f.start_date) e.start_date = 'Date required';
    const minStart = getMinimumStartTime(f.start_date, bookingStartTime);
    if (!f.start_time) e.start_time = 'Start time required';
    if (!f.end_time)   e.end_time   = 'End time required';
    if (f.start_time && f.start_time < bookingStartTime) e.start_time = 'Earliest booking time is 12:00 AM';
    // if (f.start_time && f.start_time < minStart)           e.start_time = 'Invalid start time';
    if (f.start_time && f.start_time > bookingEndTime)   e.start_time = 'Latest booking time is 11:59 PM';
    if (f.end_time   && f.end_time   > bookingEndTime)   e.end_time   = 'Latest booking time is 11:59 PM';
    if (f.start_time && f.end_time && f.start_time >= f.end_time) e.end_time = 'End must be after start';
    // Medicine/oil and notes are optional
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [
    isAssignDoctorMode,
    showTreatment2,
    treatment2.doctor_id,
    treatment2.session_fraction,
    bookingStartTime,
    bookingEndTime,
  ]);

  // ── Field setter ──────────────────────────────────────
  const set = (k, v) => {
    setConflictMessage('');
    setForm((prev) => {
      let val = v;
      if (['patient_id','package_id','package_service_id'].includes(k)) {
        val = v === '' ? '' : Number(v);
        if (Number.isNaN(val)) val = v;
      }
      if (['doctor_id','room_id'].includes(k)) {
        val = v === '' ? '' : String(v);
      }
      const updated = { ...prev, [k]: val };
      if (k === 'start_date') {
        const minStart = getMinimumStartTime(val, bookingStartTime);
        if (updated.start_time && updated.start_time < minStart)
          updated.start_time = minStart;
      }
      // Reset service selection when package changes
      if (k === 'package_id') updated.package_service_id = '';
      return updated;
    });
    if (k === 'doctor_id' && v) setActivePanel('doctor');
    if (k === 'room_id'   && v) setActivePanel('room');
    
    // Immediate validation for time fields
    if (k === 'start_time' || k === 'end_time') {
      const newErrors = { ...errors };
      if (k === 'start_time') {
        if (v && v < bookingStartTime) {
          newErrors.start_time = 'Earliest booking time is 12:00 AM';
        } else if (v && v > bookingEndTime) {
          newErrors.start_time = 'Latest booking time is 11:59 PM';
        } else {
          delete newErrors.start_time;
        }
      }
      if (k === 'end_time') {
        if (v && v > bookingEndTime) {
          newErrors.end_time = 'Latest booking time is 11:59 PM';
        } else if (v && v < bookingStartTime) {
          newErrors.end_time = 'Earliest booking time is 12:00 AM';
        } else {
          delete newErrors.end_time;
        }
      }
      setErrors(newErrors);
    }
  };

  // ── Reactively validate form whenever it changes ───────
  useEffect(() => {
    validate(form);
  }, [form, validate]);

  // Returns true when two time ranges overlap.
  const isTimeOverlap = (startA, endA, startB, endB) => {
    const startAMinutes = timeValueToMinutes(startA);
    const endAMinutes = timeValueToMinutes(endA);
    const startBMinutes = timeValueToMinutes(startB);
    const endBMinutes = timeValueToMinutes(endB);
    if (
      startAMinutes === null ||
      endAMinutes === null ||
      startBMinutes === null ||
      endBMinutes === null
    ) {
      return false;
    }
    return startAMinutes < endBMinutes && startBMinutes < endAMinutes;
  };

  const currentBookingId = editPlan?.booking_id || editPlan?.id || urlId || form.id;
  const currentSessionId = editPlan?.session_id || editPlan?.sessionId || form.session_id || form.sessionId;
  const currentVisitId = editPlan?.visit_id || editPlan?.visitId || form.visit_id || form.visitId;

  const isCurrentBooking = (bookingId) =>
    isEdit && bookingId != null && String(bookingId) === String(currentBookingId);

  const isCurrentScheduleEntry = (entry) => {
    if (!isEdit || !entry) return false;
    if (isCurrentBooking(entry.booking_id ?? entry.bookingId)) return true;

    const entrySessionId = entry.session_id ?? entry.sessionId;
    if (currentSessionId != null && entrySessionId != null &&
        String(entrySessionId) === String(currentSessionId)) return true;

    // Older occupancy responses exposed roomlog.visitid as booking_id.
    // Match the explicit visit field as a compatibility safeguard.
    const entryVisitId = entry.visitid ?? entry.visit_id ?? entry.visitId;
    if (entryVisitId == null) return false;
    return String(entryVisitId) === String(currentBookingId) ||
      (currentVisitId != null && String(entryVisitId) === String(currentVisitId));
  };

  const hasDoctorConflict = (schedule, start, end) =>
    schedule.some((s) => {
      if (isCurrentScheduleEntry(s)) return false;
      const sStart = s.start_time?.slice(0, 5);
      const sEnd = s.end_time?.slice(0, 5);
      return sStart && sEnd && isTimeOverlap(start, end, sStart, sEnd);
    });

  // Checks whether the requested session overlaps any doctor leave slot.
  const hasDoctorLeaveConflict = (leaveSlots, start, end) =>
    leaveSlots.some((slot) => isTimeOverlap(start, end, slot.start, slot.end));

  // ── Initial data load ─────────────────────────────────
  useEffect(() => { fetchDropdowns(); }, []);

  useEffect(() => {
    if (doctors.length === 0) return;
    // Include session_doctor_id as fallback since doctor may be assigned at session level
    // Also include routerLocation.state.doctor.id if injected
    const planDoctorId = String(
      editPlan?.doctor_id || editPlan?.doctorId || editPlan?.session_doctor_id || 
      editPlan?.gvId || editPlan?.gvid || editPlan?.personId || editPlan?.person_id || 
      routerLocation.state?.doctor?.id || ''
    );
    if (!planDoctorId) return;

    const matchingDoctor = doctors.find((d) => {
      const doctorId = String(getDoctorId(d));
      return (
        doctorId === planDoctorId ||
        String(d.personId) === planDoctorId ||
        String(d.person_id) === planDoctorId ||
        String(d.gvId) === planDoctorId ||
        String(d.gvid) === planDoctorId
      );
    });

    if (matchingDoctor) {
      setForm((prev) => ({ ...prev, doctor_id: String(resolveDoctorGvId(matchingDoctor) || getDoctorId(matchingDoctor)) }));
    }
  }, [doctors, editPlan, routerLocation.state?.doctor]);

  const fetchDropdowns = async () => {
    setLoadingData(true);
    try {
      const results = await Promise.all([
        bookingPlanService.getClosedVisitPatients().catch(err => {
          console.error('[BookingNewSession] Closed visit patient fetch error:', err);
          return null;
        }),
        dispatch(doctorActions.getDoctorListByType('doctor')).catch(err => {
          console.error('[BookingNewSession] Doctor fetch error:', err);
          return null;
        }),
        // Clinical sessions can be scheduled only in Treatment Rooms.
        roomServices
          .getRoomList('', [ROOM_CATEGORIES.TREATMENT_ROOM])
          .catch((err) => {
            console.error('[BookingNewSession] Room fetch error:', err);
            return null;
          }),
        bookingPlanService.getPackages().catch(err => {
          console.error('[BookingNewSession] Package fetch error:', err);
          return null;
        }),
        bookingPlanService.getPackageServices().catch(err => {
          console.error('[BookingNewSession] Service fetch error:', err);
          return null;
        }),
      ]);

      const [pRes, dRes, rRes, pkgRes, svcRes] = results;

      console.log("[BookingNewSession] Fetched Patients:", pRes);
      // console.log("Fetched Patients:", pRes);
      console.log("[BookingNewSession] Fetched Doctors:", dRes);
      console.log("[BookingNewSession] Fetched Rooms:", rRes);
      console.log("[BookingNewSession] Fetched Packages:", pkgRes);
      console.log("[BookingNewSession] Fetched Package Services:", svcRes);

      const fetchedPatients = asList(pRes?.data);
      setPatients(fetchedPatients);
      
      //   const current = routerLocation.state?.selectedPatient;
      //   if (!current) return fetchedPatients;
      //   const exists = fetchedPatients.some(
      //     (pt) => String(getEntityId(pt)) === String(getEntityId(current))
      //   );
      //   return exists ? fetchedPatients : [current, ...fetchedPatients];
      // });
      // console.log("Patient ",fetchedPatients);

      // Extract doctor list from multiple possible response structures
      // Priority: gvlist (has real objects) > data > personList (often has nulls) > raw array
     let doctorList = [];
if (Array.isArray(dRes)) {
  doctorList = dRes;
} else if (Array.isArray(dRes?.accresp?.personList)) {
  // /api/v1/doctors returns data nested under accresp.personList
  doctorList = dRes.accresp.personList;
} else if (Array.isArray(dRes?.gvlist)) {
  doctorList = dRes.gvlist;
} else if (Array.isArray(dRes?.data)) {
  doctorList = dRes.data;
} else if (Array.isArray(dRes?.personList)) {
  doctorList = dRes.personList;
}
console.log("Doctor List",doctorList);
      // Filter out null/undefined entries; also filter by status when status field exists
      let filteredDoctors = doctorList.filter((d) => {
        if (!d) return false;
        // Some APIs may not have a status field at all - include those
        if (d.status === undefined || d.status === null) return true; // no status field = include
        const s = String(d.status).toUpperCase();
        return s === 'AVAILABLE' || s === 'ACTIVE' || d.status === 1 || d.status === true;
      });

      // In "Assign Doctor" mode, show only doctors (exclude Counsellors)
      if (isAssignDoctorMode) {
        console.log('[BookingNewSession] Assign Doctor mode detected - filtering doctors');
        console.log('[BookingNewSession] Total doctors before filter:', filteredDoctors.length);
        filteredDoctors = filteredDoctors.filter((d) => {
          const type = String(d?.type || d?.doctor_type || d?.role || '').toLowerCase();
          console.log('[BookingNewSession] Checking doctor:', d?.first_name || d?.name || d?.identifier, 'type:', type);
          return type === 'doctor';
        });
        console.log('[BookingNewSession] Assign Doctor mode - filtered doctors only:', filteredDoctors.length);
      } else {
        console.log('[BookingNewSession] NOT in Assign Doctor mode, showing all doctors');
      }

      setDoctors(filteredDoctors);
console.log("Doctor",doctorList);
      // Room list can return {roomList: [...]} at root, not inside .data.
      const allRooms = asList(
        rRes?.roomList || rRes?.data?.roomList || rRes?.data?.data || rRes?.data || rRes
      );
      console.log('[BookingNewSession] rooms loaded:', allRooms.length, allRooms[0] ?? 'empty');
      setRooms(
        allRooms.filter((r) => {
          if (!r.status) return true;
          const s = String(r.status).toUpperCase();
          return s === 'ACTIVE' || s === 'AVAILABLE' || r.status === 1;
        })
      );

      const pkgList = asList(pkgRes.data);
      console.log('[BookingNewSession] packages loaded:', pkgList.length, pkgList[0] ?? 'empty');
      setPackages(pkgList);

      // Build a map of packageId -> packageName from the packages list for fast lookup
      const pkgMap = {};
      pkgList.forEach((pkg) => {
        pkgMap[String(pkg.id ?? '')] = getPackageLabel(pkg);
      });

      const allSvcList = asList(svcRes.data);
      console.log('[BookingNewSession] services loaded:', allSvcList.length, allSvcList[0] ?? 'empty');

      const combined = [];
      const seen = new Set();

      allSvcList.forEach((svc) => {
        const svcId  = String(svc.id ?? '');
        const pkgId  = String(svc.packageid ?? svc.packageId ?? '');
        // Prefer the name from our loaded packages list; fall back to the embedded packagename on the service
        const pkgName = pkgMap[pkgId] || svc.packagename || svc.packageName || '';
        const svcName  = getServiceLabel(svc);
        const svcType  = getServiceType(svc);
        const key = `${pkgId}_${svcId}`;
        if (seen.has(key)) return; // deduplicate
        seen.add(key);
        combined.push({
          value: key,
          label: svcType ? `${pkgName}  ${svcName} (${svcType})` : `${pkgName}  ${svcName}`,
          package_id: pkgId,
          package_service_id: svcId,
          pkgName,
          svcName,
        });
      });

      setCombinedPackageServices(combined);
      console.log('[BookingNewSession] combined package-services:', combined.length, combined[0] ?? 'empty');

    } catch (err) {
      console.error('[BookingNewSession] fetchDropdowns failed:', err);
      toast.error('Failed to load form data');
    } finally {
      setLoadingData(false);
    }
  };



  // ── Edit mode: populate form ──────────────────────────
  useEffect(() => {
    if (!editPlan) return;
    const start = getMinimumStartTime(
      editPlan.start_date || toDateInputValue(),
      bookingStartTime
    );
    // FIX: Also check routerLocation.state?.doctor?.id as fallback for doctor_id.
    // When navigating from SearchDocList Edit button, the current assigned doctor is
    // passed in navigation state and may be more  up-to-date than the fetched plan.
    const resolvedDoctorId = String(
      editPlan.doctor_id || editPlan.session_doctor_id ||
      routerLocation.state?.doctor?.id || ''
    );
    setForm({
      ...editPlan,
      booking_mode:     editPlan.booking_mode || 'date',
      doctor_id:        resolvedDoctorId,
      room_id:          editPlan.room_id      || '',
      package_id:       editPlan.package_id || '',
      package_service_id: editPlan.package_service_id || '',
      product_id:       editPlan.product_id || '',
      product_name:     editPlan.product_name || '',
      category_name:    editPlan.category_name || '',
      session_fraction: `${editPlan.session_number || 1}/${editPlan.total_sessions || 1}`,
      start_time:       editPlan.start_time?.slice(0, 5) || start,
      end_time:         editPlan.end_time?.slice(0, 5)   || addMinutesToTime(start, 60),
      type:             editPlan.type || 'Doctor',
      counselor:        editPlan.counselor || '',
      // Store visitassignmentid so buildBookingPlanPayload can pass it to backend
      // to skip room validation when editing an existing booking
      visitassignmentid: editPlan.visitassignmentid || null,
    });

    // Populate the second "Doctor and treatment information (2)" card if the
    // booking plan being edited has any secondary treatment data saved.
    if (editPlan.doctor_id2 || editPlan.package_id2 || editPlan.package_service_id2 || editPlan.treatment_name2 ||
        editPlan.medicine_oil2 || editPlan.notes2 || editPlan.total_sessions2) {
      setShowTreatment2(true);
      setTreatment2({
        doctor_id:           String(editPlan.doctor_id2 || ''),
        package_id:          editPlan.package_id2 || '',
        package_service_id:  editPlan.package_service_id2 || '',
        session_fraction:    `${editPlan.session_number2 || 1}/${editPlan.total_sessions2 || 1}`,
        medicine_oil:        editPlan.medicine_oil2 || '',
        notes:               editPlan.notes2 || '',
      });
      if (editPlan.package_name2 || editPlan.package_service_name2 || editPlan.treatment_name2) {
        setPackageSearch2(
          [editPlan.package_name2, editPlan.package_service_name2].filter(Boolean).join(' - ') ||
          (editPlan.treatment_name2 !== 'General Treatment' ? editPlan.treatment_name2 : '')
        );
      }
    }

    // Populate product search display if product info exists
    if (editPlan.product_name) {
      const displayLabel = editPlan.category_name
        ? `${editPlan.product_name}(${editPlan.category_name})`
        : editPlan.product_name;
      setProductSearch(displayLabel);
      setSelectedProduct({
        productId: editPlan.product_id || '',
        name: editPlan.product_name || '',
        productName: editPlan.product_name || '',
        categoryName: editPlan.category_name || '',
        productType: editPlan.category_name || '',
      });
    }

    // Populate package search display if package+service info exists
    if (editPlan.package_id && editPlan.package_service_id) {
      if (combinedPackageServices.length > 0) {
        const matched = combinedPackageServices.find(
          (c) => String(c.package_id) === String(editPlan.package_id) &&
                 String(c.package_service_id) === String(editPlan.package_service_id)
        );
        if (matched) {
          setPackageSearch(matched.label);
        } else if (editPlan.package_name || editPlan.package_service_name) {
          setPackageSearch(
            [editPlan.package_name, editPlan.package_service_name].filter(Boolean).join(' - ')
          );
        }
      } else if (editPlan.package_name || editPlan.package_service_name) {
        setPackageSearch(
          [editPlan.package_name, editPlan.package_service_name].filter(Boolean).join(' - ')
        );
      }
    } else if (editPlan.package_name || editPlan.package_service_name || editPlan.treatment_name) {
      // No package_id/service_id at all — fall back to whatever name fields exist
      setPackageSearch(
        [editPlan.package_name, editPlan.package_service_name].filter(Boolean).join(' - ') ||
        (editPlan.treatment_name && editPlan.treatment_name !== 'General Treatment' ? editPlan.treatment_name : '')
      );
    }

    // Populate patient search display
    if (editPlan.patient_id || editPlan.patientId) {
      setSelectedPatient((currentPatient) => ({
        ...currentPatient,
        id: editPlan.patient_id || editPlan.patientId,
        fname: editPlan.patient_name || editPlan.patientName || editPlan.fname || editPlan.first_name || '',
        phone: editPlan.phone || editPlan.phno || '',
        internalStatus:
          currentPatient?.internalStatus ||
          currentPatient?.visit?.internalStatus ||
          editPlan.internalStatus ||
          routerLocation.state?.visitInternalStatus ||
          null,
      }));
    }

  }, [
    editPlan,
    combinedPackageServices,
    routerLocation.state?.doctor?.id,
    routerLocation.state?.visitInternalStatus,
    bookingStartTime,
  ]);

  // ── Patient Search Helpers ─────────────────────────────
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [injectedPatient, setInjectedPatient] = useState(null);

  const selectPatient = (patient) => {
    setSelectedPatient(patient);

    setForm((prev) => ({
      ...prev,
      patient_id: patient.id || patient.patient_id || patient.patientId || '',
      patient_name: patient.patient_name || patient.fname || patient.first_name || patient.name || '',
      phone: patient.phone || patient.phno || patient.phoneno || '',
    }));
  };

  // ── Patient injected from another page ────────────────
  useEffect(() => {
    const sel = routerLocation.state?.selectedPatient;
    if (!sel) return;
    const norm = normalizePatientRecord(sel);
    if (!norm.id) return;
    setInjectedPatient(norm);
    setSelectedPatient(norm);
    setPatients((prev) => {
      const list   = Array.isArray(prev) ? prev : [];
      const exists = list.some((p) => String(p.id) === String(norm.id));
      return exists ? list : [norm, ...list];
    });
    
    // Build form updates - start with patient info
    const formUpdates = {
      patient_id: parseInt(norm.id, 10),
      patient_name: norm.fullName || norm.fname,
      phone: norm.phone || norm.phno,
    };
    
    // Pre-populate doctor if passed from navigation state
    const doctorInfo = routerLocation.state?.doctor || (norm.assignedDoctorId ? { id: norm.assignedDoctorId, name: norm.assignedType || '' } : null);
    if (doctorInfo?.id) {
      formUpdates.doctor_id = String(doctorInfo.id);
      console.log('[BookingNewSession] Pre-selecting doctor:', doctorInfo.id, doctorInfo.name);
    }
    
    // Pre-populate room if passed from navigation state
    const roomInfo = routerLocation.state?.room;
    if (roomInfo?.id) {
      formUpdates.room_id = String(roomInfo.id);
      console.log('[BookingNewSession] Pre-selecting room:', roomInfo.id, roomInfo.name);
    }
    
    setForm((prev) => ({
      ...prev,
      ...formUpdates,
    }));
    
    toast.success('Patient selected for booking');
    // Remove only the one-time patient payload. Keep mode, existingVisitId,
    // hasOpenVisit and any fetched plan; the submit flow needs those values.
    const updatedState = { ...(routerLocation.state || {}) };
    delete updatedState.selectedPatient;
    
    history.replace({
      pathname: routerLocation.pathname,
      state: updatedState,
    });
  }, [routerLocation.state, history, routerLocation.pathname]);
// ── Recover patient identity after a browser refresh ──────────────
// routerLocation.state (selectedPatient / plan) is wiped right after it's
// consumed, and is lost entirely on a hard refresh. urlPatientId survives
// though (it's in the URL), so use it to re-fetch the patient whenever we
// land here without a name already available.
useEffect(() => {
  if (!urlPatientId) return;
  if (form.patient_name || selectedPatient?.fname || selectedPatient?.fullName) return;

  let cancelled = false;
  // getPatientProfile is a callback-style thunk (see patientActions.js) — it does
  // not return a promise from dispatch(), so `await dispatch(...)` always resolves
  // to undefined. Use the resCb callback instead, as every other caller does.
  dispatch(patientActions.getPatientProfile(urlPatientId, urlVisitId || undefined, (raw) => {
    if (cancelled) return;
    const norm = normalizePatientRecord(raw);
    if (!norm?.id) {
      console.error('[BookingNewSession] Failed to recover patient after refresh:', raw);
      return;
    }

    setSelectedPatient((prev) => prev || norm);
    setForm((prev) => (
      prev.patient_name
        ? prev
        : {
            ...prev,
            patient_id: prev.patient_id || norm.id,
            patient_name: norm.fullName || norm.fname,
            phone: prev.phone || norm.phone,
          }
    ));
  }));
  return () => { cancelled = true; };
}, [urlPatientId]); // eslint-disable-line react-hooks/exhaustive-deps
  // ── Doctor schedule ───────────────────────────────────
  useEffect(() => {
    if (activePanel !== 'doctor' || !form.doctor_id || !form.start_date) {
      setDoctorSchedule([]);
      return;
    }
    let cancelled = false;
    const doFetch = async () => {
      setLoadingSchedule(true);
      try {
        const res  = await bookingPlanService.getDoctorSchedule(form.doctor_id, form.start_date);
        const data = res?.data?.data || res?.data || [];
        if (!cancelled) setDoctorSchedule(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('[BookingNewSession] Doctor schedule fetch failed:', err);
        if (!cancelled) setDoctorSchedule([]);
      } finally {
        if (!cancelled) setLoadingSchedule(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [form.doctor_id, form.start_date, activePanel]);

  // ── Second doctor schedule (Doctor and treatment information (2)) ────
  useEffect(() => {
    if (activePanel !== 'doctor' || !showTreatment2 || !treatment2.doctor_id || !form.start_date) {
      setDoctorSchedule2([]);
      return;
    }
    let cancelled = false;
    const doFetch = async () => {
      setLoadingSchedule2(true);
      try {
        const res  = await bookingPlanService.getDoctorSchedule(treatment2.doctor_id, form.start_date);
        const data = res?.data?.data || res?.data || [];
        if (!cancelled) setDoctorSchedule2(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('[BookingNewSession] Second doctor schedule fetch failed:', err);
        if (!cancelled) setDoctorSchedule2([]);
      } finally {
        if (!cancelled) setLoadingSchedule2(false);
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [treatment2.doctor_id, form.start_date, activePanel, showTreatment2]);

  // ── Doctor leave slots (blocks booking when the doctor is marked
  // unavailable at the selected time) — fetched independent of activePanel
  // so the submit-time check works even when the checker grid isn't open.
  useEffect(() => {
    if (!form.doctor_id || !form.start_date) {
      setDoctorLeaveSlots([]);
      return;
    }
    let cancelled = false;
    dispatch(patientActions.getleaveCalender(form.doctor_id, form.start_date, (resCb) => {
      if (cancelled) return;
      const leaveMap = resCb?.lc || resCb?.data?.lc || resCb?.data || {};
      setDoctorLeaveSlots(parseLeaveMapToSlots(
        leaveMap,
        bookingStartTime,
        bookingEndTime,
        doctorLeaveUnavailableSlotValue
      ));
    }));
    return () => { cancelled = true; };
  }, [
    form.doctor_id,
    form.start_date,
    dispatch,
    bookingStartTime,
    bookingEndTime,
    doctorLeaveUnavailableSlotValue,
  ]);

  // ── Second doctor's leave slots ───────────────────────────
  useEffect(() => {
    if (!showTreatment2 || !treatment2.doctor_id || !form.start_date) {
      setDoctorLeaveSlots2([]);
      return;
    }
    let cancelled = false;
    dispatch(patientActions.getleaveCalender(treatment2.doctor_id, form.start_date, (resCb) => {
      if (cancelled) return;
      const leaveMap = resCb?.lc || resCb?.data?.lc || resCb?.data || {};
      setDoctorLeaveSlots2(parseLeaveMapToSlots(
        leaveMap,
        bookingStartTime,
        bookingEndTime,
        doctorLeaveUnavailableSlotValue
      ));
    }));
    return () => { cancelled = true; };
  }, [
    treatment2.doctor_id,
    form.start_date,
    showTreatment2,
    dispatch,
    bookingStartTime,
    bookingEndTime,
    doctorLeaveUnavailableSlotValue,
  ]);

  // Jump the checker to the second doctor's grid as soon as one is picked;
  // fall back to the primary doctor if the second doctor is cleared/removed.
  useEffect(() => {
    if (showTreatment2 && treatment2.doctor_id) {
      setCheckerDoctorSlot('secondary');
    } else {
      setCheckerDoctorSlot('primary');
    }
  }, [showTreatment2, treatment2.doctor_id]);

  // ── Room occupancy fetch for conflict checking ───────────
  useEffect(() => {
    if (!form.start_date) {
      setRoomOccupancy({});
      return;
    }
    let cancelled = false;
    const fetchOccupancy = async () => {
      try {
        const res = await roomServices.getRoomOccupancy(form.start_date, 1);
        const data = res?.data?.data || res?.data || res || {};
        if (!cancelled) setRoomOccupancy(typeof data === 'object' ? data : {});
      } catch (err) {
        console.error('[BookingNewSession] Room occupancy fetch failed:', err);
        if (!cancelled) setRoomOccupancy({});
      }
    };
    fetchOccupancy();
    return () => { cancelled = true; };
  }, [form.start_date]);

  const getRoomBookings = useCallback((roomId) => {
    const roomData = roomOccupancy?.[roomId];
    if (Array.isArray(roomData)) return roomData.filter(Boolean);
    if (!roomData || typeof roomData !== 'object') return [];
    return Object.values(roomData).flat().filter(Boolean);
  }, [roomOccupancy]);

  const hasFullDayRoomBlock = useCallback((roomId) =>
    getRoomBookings(roomId).some((slot) => {
      const bookStatus = String(slot?.bookstatus || '').toLowerCase();
      return bookStatus === 'booked' && !slot?.end_time;
    }), [getRoomBookings]);

  const selectableRooms = useMemo(() =>
    rooms.filter((room) => !hasFullDayRoomBlock(String(getEntityId(room)))),
    [rooms, hasFullDayRoomBlock]
  );

  const hasRoomConflict = (roomId, start, end) =>
    getRoomBookings(roomId).some((slot) => {
      if (isCurrentScheduleEntry(slot)) {
        return false;
      }
      const sStart = slot.start_time?.slice(0, 5);
      const sEnd = slot.end_time?.slice(0, 5);
      if (!sEnd) return true;
      return sStart && sEnd && isTimeOverlap(start, end, sStart, sEnd);
    });

  // ── Submit ────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("SUBMIT CLICKED");
    
    // ═══════════════════════════════════════════════════════════════
    // CHECK FOR EXISTING VISIT - Use existing visit ID instead of creating new
    // ═══════════════════════════════════════════════════════════════
    // Patient search now returns both open- and closed-visit patients. When
    // the picked patient already has an open visit (visit_status: 'open'),
    // reuse that visit id the same way the PatientSections "existing visit"
    // flow does, instead of running the new-visit validation/create path.
    const searchedOpenVisitId = (() => {
      const status = String(
        selectedPatient?.visit_status || selectedPatient?.visitStatus || ''
      ).trim().toLowerCase();
      if (status !== 'open') return null;
      return selectedPatient?.visit_id ?? selectedPatient?.visitId ?? null;
    })();
    const existingVisitId = routerLocation.state?.existingVisitId ?? urlVisitId ?? searchedOpenVisitId;
    // A visit ID in this PatientSections route means we must reuse that visit.
    // Never run the new-visit validation/create path for it.
    const hasOpenVisit = Boolean(existingVisitId);
    
    if (!validate(form)) {
      console.log("VALIDATION FAILED", form, errors);
      toast.error('Please fix the validation errors before submitting.');
      return;
    }
    console.log("VALIDATION PASSED", form);

    const requiresDoctor = isDoctorBookingMode(form.booking_mode);

    // Find selected doctor from doctors list
    const selectedDoctor = doctors.find((d) =>
      String(resolveDoctorGvId(d) || getDoctorId(d)) === String(form.doctor_id)
    );

    // Extract doctor ID - ensure it's a valid numeric value for the backend
    let gvId = null;
    if (selectedDoctor) {
      const rawId = resolveDoctorGvId(selectedDoctor) || getDoctorId(selectedDoctor);
      gvId = rawId && !isNaN(Number(rawId)) ? Number(rawId) : null;
    } else if (form.doctor_id) {
      gvId = !isNaN(Number(form.doctor_id)) ? Number(form.doctor_id) : null;
    }

    const hasDoctor = Boolean(gvId);
    const hasRoom   = Boolean(form.room_id);

    const selectedDoctorType = String(
      selectedDoctor?.type || selectedDoctor?.doctor_type || selectedDoctor?.role || ''
    ).trim();
    const isCounsellor = selectedDoctorType.toLowerCase() === 'counsellor';
    const appointmentType = isCounsellor ? 'Counsellor' : 'Doctor';

    // Build Visit and VisitAssignment objects for patientBP.updateVisit()
    // This ensures the doctor or counsellor assignment is persisted in visitassignment table
    const visitObj = {
      id: urlVisitId ? Number(urlVisitId) : null,
      patientId: form.patient_id ? Number(form.patient_id) : null,
      apptdate: form.start_date || null,
      appthours: form.start_time ? form.start_time.split(':')[0] : null,
      apptminutes: form.start_time ? form.start_time.split(':')[1] : null,
      type: appointmentType,
      internalStatus:
        selectedPatient?.internalStatus ||
        selectedPatient?.visit?.internalStatus ||
        null,
    };

    const visitAssignmentObj = hasDoctor ? {
      visitid: urlVisitId ? Number(urlVisitId) : null,
      targetid: gvId,
      type: appointmentType,
      status: "open",
      groupname: null,
    } : null;

    // Conflict check logic:
    // - Room conflicts always apply when a room is selected
    // - Doctor double-bookings are allowed: same doctor can be assigned to multiple patients at the same time slot
    // - Doctor leave/unavailable slots always block booking, regardless of double-booking policy
    // - By Date bookings skip doctor checks entirely (no doctor is selected)
    if (hasRoom) {
      if (hasRoomConflict(form.room_id, form.start_time, form.end_time)) {
        setConflictMessage('Selected room is unavailable for the chosen time.');
        return;
      }
    }

    let latestDoctorLeaveSlots = doctorLeaveSlots;
    let latestDoctorLeaveSlots2 = doctorLeaveSlots2;
    try {
      const leaveChecks = [
        hasDoctor
          ? fetchLatestDoctorLeaveSlots(gvId, form.start_date)
          : Promise.resolve([]),
        showTreatment2 && treatment2.doctor_id
          ? fetchLatestDoctorLeaveSlots(treatment2.doctor_id, form.start_date)
          : Promise.resolve([]),
      ];
      [latestDoctorLeaveSlots, latestDoctorLeaveSlots2] = await Promise.all(
        leaveChecks
      );
      setDoctorLeaveSlots(latestDoctorLeaveSlots);
      setDoctorLeaveSlots2(latestDoctorLeaveSlots2);
    } catch (error) {
      setConflictMessage(
        'Unable to verify doctor availability. Please try again.'
      );
      return;
    }

    if (hasDoctor && hasDoctorLeaveConflict(latestDoctorLeaveSlots, form.start_time, form.end_time)) {
      setConflictMessage('Selected doctor is not available for the chosen time.');
      return;
    }

    if (showTreatment2 && treatment2.doctor_id &&
        hasDoctorLeaveConflict(latestDoctorLeaveSlots2, form.start_time, form.end_time)) {
      setConflictMessage('Selected doctor (2) is not available for the chosen time.');
      return;
    }

    setSaving(true);
    setConflictMessage('');
    try {
      const payload = buildBookingPlanPayload({
        formData: form,
        selectedProduct,
        packageSearchText: packageSearch,
        packageSearchText2: packageSearch2,
        doctorsList: doctors,
        existingVisitId,
        urlVisitId,
        doctorDepartmentId: routerLocation.state?.doctorDepartmentId,
        locationState: routerLocation.state,
        selectedPatient,
        isAssignDoctorMode,
        treatment2: showTreatment2 ? treatment2 : null,
      });

      if (!isAssignDoctorMode) {
        payload.groupname = BOOKING_PLAN_GROUP_NEW_SESSION;
        if (payload.va) {
          payload.va.groupname = BOOKING_PLAN_GROUP_NEW_SESSION;
        }
      }
      
      console.log('[BookingNewSession] Submitting payload:', {
        visitId: payload.visit_id,
        visitassignmentid: payload.visitassignmentid,
        groupname: payload.groupname,
        doctor_id: payload.doctor_id,
        mode: routerLocation.state?.mode,
      });

      if (isEdit) {
        // ── Detect changes between original editPlan and current form ──────────
        const originalDoctorStr = String(editPlan?.doctor_id || editPlan?.session_doctor_id || editPlan?.doctorId || '');
        const formDoctorStr     = String(form?.doctor_id || '');
        const doctorChanged     = originalDoctorStr && formDoctorStr && originalDoctorStr !== formDoctorStr;
        const originalDate      = String(editPlan?.session_date || editPlan?.start_date || '').slice(0, 10);
        const originalStartTime = String(editPlan?.start_time || '').slice(0, 5);
        const originalEndTime   = String(editPlan?.end_time || '').slice(0, 5);
        const sameTimeSlot      =
          originalDate === String(form?.start_date || '').slice(0, 10) &&
          originalStartTime === String(form?.start_time || '').slice(0, 5) &&
          originalEndTime === String(form?.end_time || '').slice(0, 5);
        
        const originalRoomId = String(editPlan?.room_id || editPlan?.roomId || '');
        const currentRoomId = String(form?.room_id || '');
        const roomChanged = originalRoomId && currentRoomId && originalRoomId !== currentRoomId;
        
        const currentStartTime = String(form?.start_time || '').slice(0, 5);
        const currentEndTime = String(form?.end_time || '').slice(0, 5);
        const timeSlotChanged = originalStartTime && currentStartTime && 
                                (originalStartTime !== currentStartTime || originalEndTime !== currentEndTime);
        
        // Reassign (cancel old + create new) whenever room, doctor, or time slot
        // changes. Same-room reassign is handled by transferRoomReservationForReassignedSession
        // on the backend, and the pre-submit hasRoomConflict check already excludes
        // the current booking, so a doctor-only change no longer needs the plain
        // update path to avoid a self-conflict.
        const shouldReassign = roomChanged || doctorChanged || timeSlotChanged;

        if (shouldReassign) {
          let reassignReason = '';
          if (roomChanged) {
            const oldRoomLabel = editPlan?.room_number || editPlan?.room_code || `Room ID: ${originalRoomId}`;
            const newRoomLabel = currentRoomId;
            reassignReason = `Room changed from ${oldRoomLabel} to ${newRoomLabel}`;
          } else if (doctorChanged) {
            const oldDoctorLabel = editPlan?.doctor_name || `Doctor ID: ${originalDoctorStr}`;
            const newDoctorLabel = (selectedDoctor && getDoctorLabel(selectedDoctor)) || `Doctor ID: ${formDoctorStr}`;
            reassignReason = `Doctor changed from ${oldDoctorLabel} to ${newDoctorLabel}`;
          } else if (timeSlotChanged) {
            reassignReason = `Time slot changed from ${originalStartTime}-${originalEndTime} to ${currentStartTime}-${currentEndTime}`;
          }
          
          payload.reassign = true;
          payload.reassignComment = reassignReason;
          
          console.log(`[BookingNewSession] ${reassignReason} - triggering reassign (cancel old + create new)`);
        }

        const updateResponse = await bookingPlanService.updateBookingPlan(form.id, payload);
        console.log('[BookingNewSession] Update response:', updateResponse);
        toast.success(
          shouldReassign
            ? '✅ Booking Plan updated successfully '
            : '✅ Booking Plan updated successfully'
        );
      } else {
        let createResponse = null;

        if (hasOpenVisit && existingVisitId) {
          console.log('[BookingNewSession] Creating booking plan for existing visit:', existingVisitId);
          console.log('[BookingNewSession] Skipping validation API call - using existing visit');

          createResponse = await bookingplanService.createBookingPlanForExistingVisit(payload);
          console.log('[BookingNewSession] Create for existing visit response:', createResponse);

          if (createResponse?.success === false || createResponse?.data?.success === false) {
            const errorMsg = createResponse?.message || createResponse?.data?.message || 'Unknown error';
            toast.error('❌ Failed to create booking plan: ' + errorMsg);
            setSaving(false);
            return;
          }

          toast.success('✅ Booking plan created successfully for existing visit');
        } else {
          try {
            await validateBookingPlan(payload);
          } catch (validationErr) {
            if (validationErr?.status === 420 || validationErr?.response?.status === 420) {
              const errorMsg = validationErr?.data?.message || validationErr?.data?.data ||
                              'Patient already has an open visit. Please close the existing visit before creating a new booking plan.';
              setErrorModalMessage(errorMsg);
              setShowErrorModal(true);
              setSaving(false);
              return;
            }
            throw validationErr;
          }

          createResponse = await bookingPlanService.createBookingPlan(payload);
          toast.success('✅ Booking Plan created successfully');
        }
        
        // Extract new visitId from response to ensure we fetch fresh data with correct visitId
        let newVisitId = urlVisitId; // fallback to old value
        if (createResponse?.data?.visit?.id) {
          newVisitId = createResponse.data.visit.id;
        } else if (createResponse?.visit?.id) {
          newVisitId = createResponse.visit.id;
        } else if (createResponse?.data?.visit_id) {
          newVisitId = createResponse.data.visit_id;
        }
        
        console.log('[BookingNewSession] New visitId after booking:', newVisitId);
        
        // Refresh patient data with CORRECT visitId to get updated visitAssignmentList
        if (form.patient_id) {
          try {
            // Refresh patient profile with new visitId
            await dispatch(patientActions.getPatientProfile(form.patient_id, newVisitId || undefined));
            
            // Refresh visit list with new visitId to get updated visitAssignmentList with doctor/room
            const visitListAction = patientActions.getVisitListById(newVisitId || form.patient_id, 'booking');
            await dispatch(visitListAction);
            
            console.log('[BookingNewSession] Patient data refreshed with correct visitId');
          } catch (err) {
            console.error('Failed to refresh patient data:', err);
          }
        }
      }
      
      const returnTo = routerLocation.state?.returnTo;
      if (returnTo) {
        history.push({ pathname: returnTo, state: { refreshRoom: Date.now() } });
      } else {
        history.push('/booking-plans');
      }
    } catch (err) {
      const action = isEdit ? 'update' : 'create';
      
      // Check if this is a 420 validation error (patient has open visit)
      if (err?.status === 420 || err?.response?.status === 420) {
        const errorMsg = err?.data?.message || err?.data?.data || 
                        'Patient already has an open visit. Please close the existing visit before creating a new booking plan.';
        setErrorModalMessage(errorMsg);
        setShowErrorModal(true);
        setSaving(false);
        return;
      }
      
      let errorMsg = `Error: Cannot ${action} booking plan. Please try again.`;
      
      // Try to extract a user-friendly message from various API error response formats
      const rawMsg = err?.data?.message || err?.data?.data || err?.message || '';
      
      if (typeof err === 'string') {
        errorMsg = `Error: Cannot ${action} booking plan. ${err}`;
      } else if (rawMsg && rawMsg !== errorMsg) {
        // Remove technical prefixes like "Error creating booking plan:" for cleaner display
        const cleanMsg = rawMsg
          .replace(/^Error (creating|updating) booking plan[:\s]*/i, '')
          .replace(/^Error[:\s]*/i, '')
          .trim();
        errorMsg = `Error: Cannot ${action} booking plan. ${cleanMsg || 'Please try again.'}`;
      }
      
      console.error('[BookingNewSession] Submit error:', err);
      setConflictMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Patient search now surfaces both open- and closed-visit patients; when the
  // selected one already has an open visit, submission reuses that visit
  // instead of creating a new one — flag it here so the form can say so.
  const selectedPatientHasOpenVisit = String(
    selectedPatient?.visit_status || selectedPatient?.visitStatus || ''
  ).trim().toLowerCase() === 'open';

  const selectedDoctorName = (() => {
    if (!form.doctor_id) return null;
    // Use gvId resolution to match how doctor option values are built
    const doc = doctors.find((d) =>
      String(resolveDoctorGvId(d) || getDoctorId(d)) === String(form.doctor_id) ||
      String(d.personId) === String(form.doctor_id) ||
      String(d.id) === String(form.doctor_id)
    );
    return doc ? getDoctorLabel(doc) : null;
  })();

  const selectedDoctorName2 = (() => {
    if (!treatment2.doctor_id) return null;
    const doc = doctors.find((d) =>
      String(resolveDoctorGvId(d) || getDoctorId(d)) === String(treatment2.doctor_id) ||
      String(d.personId) === String(treatment2.doctor_id) ||
      String(d.id) === String(treatment2.doctor_id)
    );
    return doc ? getDoctorLabel(doc) : null;
  })();

  // ── Render ────────────────────────────────────────────
  // Layout:
  //   Row 1: Patient                                       (full width)
  //   Row 2-left: Schedule Details | Treatment Info        (2-col row)
  //              Doctor            | Room
  //                                | Additional Information
  // console.log("Selected patient:", selPatient);
  const selPatient = routerLocation.state?.selectedPatient;
    console.log("Selected patient:", selPatient);
// ── Render ────────────────────────────────────────────
  // Use the form or component state since routerLocation.state is intentionally wiped after mount
  const patientName = form.patient_name || selectedPatient?.fullName || selectedPatient?.fname || selectedPatient?.patient_name || 'Patient';
  console.log("Current Patient Name for Breadcrumbs:", patientName);
  // const patientName = selPatient?.patient_name || selPatient?.fname || 'Patient';
  const fromPatientAppointment = routerLocation.state?.from === 'patientAppointment';
  const isPatientAppointmentRoute = routerLocation.pathname.includes('/new-booking');

  // For patient appointment flow, get patientId and visitId from URL params or location state
  const patientId = urlPatientId || selPatient?.id || '';
  const visitId = urlVisitId || '';

  // Determine if we should show breadcrumbs (only from patient appointment flow)
  const showBreadcrumbs = isPatientAppointmentRoute || fromPatientAppointment;

const breadcrumbItems = showBreadcrumbs
  ? [
      { label: 'Patient', path: '/Home' },  // Navigates to home/patient listing page
      ...(patientName
        ? [{ label: patientName, path: patientId ? `/PatientSections/${patientId}/${visitId}` : undefined }]
        : []),
      {
        label: isAssignDoctorMode
          ? (isEdit ? 'Edit Assign Doctor' : 'Assign Doctor')
          : (isEdit ? 'Update Booking Plan' : 'Booking Session'),
      },
    ]
  : []; // No breadcrumbs for other flows (e.g., "New Booking" from booking plans)
  // Hide optional sections when in assign doctor mode
  const hideOptionalBookingSections = isAssignDoctorMode;

  return (
    // <div className="page-container fade-in">
    <div className="page-container fade-in" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
       
        title={isEdit ? 'Update Clinical Session' : 'New Clinical Session'}
        subtitle={hideOptionalBookingSections ? 'Assign doctor and schedule room for selected patient' : 'Schedule and configure patient treatments'}
      >
        <Breadcrumbs crumbs={breadcrumbItems} />
      </PageHeader>

      {loadingData && (
        <div style={{ padding: '8px 0', fontSize: 13, color: '#64748b' }}>
          Loading form data…
        </div>
      )}

<div className="session-layout" style={{ 
  gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.8fr)',
  marginTop: '0px',       // Removes the gap
  paddingTop: '0px',      // Removes any internal top padding
  flex: 1,                // Takes up the remaining viewport height
  overflow: 'hidden'      // Stops the main layout from overflowing
}}>        {/* LEFT — Form */}
        {/* <div className="form-wrapper"> */}
        <div className="form-wrapper" style={{ overflowY: 'auto', paddingRight: '12px', paddingBottom: '20px' }}>
          <form onSubmit={handleSubmit}>

            {/* ── Row 1: Patient (full width) ─────────────────────── */}
            <div className="form-section compact-section">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PatientSearchInput
                    label="Search Patient"
                    preloadedPatients={patients}
                    value={
                      form.patient_id
                        ? (selectedPatient?.fullName ||
                           [selectedPatient?.fname, selectedPatient?.mname, selectedPatient?.lname].filter(Boolean).join(' ') ||
                           selectedPatient?.patient_name || selectedPatient?.first_name || '')
                        : ''
                    }
                    error={errors.patient_id}
                    placeholder="Type Name / Phone / Aadhaar / UHID / Patient Code (min 3 chars)…"
                    restrictToClosedVisits={true}
                    onSelect={(patient) => {
                      selectPatient(patient);
                    }}
                    disabled={Boolean(injectedPatient)}
                    onClear={() => {
                      setSelectedPatient(null);
                      setInjectedPatient(null);
                      setForm((prev) => ({ ...prev, patient_id: '', patient_name: '', phone: '' }));
                    }}
                    onAddNewPatient={() => setShowPatientForm(true)}
                  />
                  {/* {selectedPatientHasOpenVisit && (
                    <div style={{
                      marginTop: 6, fontSize: 12, color: '#15803d',
                      background: '#f0fdf4', border: '1px solid #bbf7d0',
                      borderRadius: 6, padding: '6px 10px',
                    }}>
                      This patient has an open visit — the new session will be added to it.
                    </div>
                  )} */}
                </div>
              </div>
            </div>

            {/* ── Row 2: Schedule Details (left) + Room (right) ─── */}
            <div className="form-row-2col">

              {/* Schedule Details */}
              <div className="form-section compact-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                  <h3 className="form-section-title compact-title" style={{ margin: 0 }}>Schedule Details</h3>
                  {!isAssignDoctorMode && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 500, color: '#334155', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={form.booking_mode === 'date'}
                        onChange={(e) => set('booking_mode', e.target.checked ? 'date' : 'doctor')}
                      />
                      Book date only (mode by date)
                    </label>
                  )}
                </div>
                {errors.booking_mode && (
                  <div className="invalid-feedback d-block" style={{ marginTop: -6, marginBottom: 8 }}>
                    {errors.booking_mode}
                  </div>
                )}
                <div className="form-group compact-group">
                  <label className="form-label form-required">Date & Time</label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="date"
                      className={`form-control${errors.start_date ? ' is-invalid' : ''}`}
                      value={form.start_date}
                      onChange={(e) => set('start_date', e.target.value)}
                      style={{ flex: '0 0 auto', width: 155 }}
                    />
                    <TimePicker12Hour
                      value={form.start_time}
                      onChange={(val) => set('start_time', val)}
                      error={errors.start_time}
                      bookingStartTime={bookingStartTime}
                      bookingEndTime={bookingEndTime}
                    />
                    <span style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>–</span>
                    <TimePicker12Hour
                      value={form.end_time}
                      onChange={(val) => set('end_time', val)}
                      error={errors.end_time}
                      bookingStartTime={bookingStartTime}
                      bookingEndTime={bookingEndTime}
                    />

                  </div>
                  {(errors.start_date || errors.start_time || errors.end_time) && (
                    <div className="invalid-feedback d-block">
                      {errors.start_date || errors.start_time || errors.end_time}
                    </div>
                  )}
                </div>
              </div>

              {/* Room */}
              {!isAssignDoctorMode && (
                <div className="form-section compact-section">
                  <h3 className="form-section-title compact-title">Room</h3>
                  <div className="form-group compact-group">
                    <label className="form-label">
                      Room
                      {form.room_id && (
                        <span style={{ fontSize: 11, color: '#2563eb', marginLeft: 8, cursor: 'pointer', fontWeight: 500 }}
                          onClick={() => setActivePanel('room')}>
                          View availability →
                        </span>
                      )}
                    </label>
                    <SearchableSelect
                      label=""
                      options={[
                        { value: '', label: 'Select Room (Optional)' },
                        ...selectableRooms.map((r) => ({
                          value: String(getEntityId(r)),
                          label: getRoomLabel(r),
                        })),
                      ]}
                      value={String(form.room_id)}
                      onChange={(v) => set('room_id', v)}
                      placeholder="Search room..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ── Row 3: Doctor and treatment information (full width, up to 2 cards) ─── */}
            <TreatmentInfoCard
              title="Doctor and treatment information"
              doctors={doctors}
              doctorId={form.doctor_id}
              onDoctorChange={(v) => set('doctor_id', v)}
              doctorError={errors.doctor_id}
              doctorOptional={!isDoctorBookingMode(form.booking_mode)}
              showViewSchedule
              onViewSchedule={() => setActivePanel('doctor')}
              packageSearch={packageSearch}
              onPackageSearchChange={(value) => {
                setPackageSearch(value);
                // Manual typing means the previous catalogue selection no longer
                // represents the input, so remove its IDs and store the text.
                setForm((prev) => ({
                  ...prev,
                  package_id: '',
                  package_service_id: '',
                  treatment_name: value,
                }));
              }}
              packageOpen={packageOpen}
              setPackageOpen={setPackageOpen}
              packageResults={packageResults}
              searchingPackage={searchingPackage}
              onSelectPackage={(item) => {
                setForm((prev) => ({
                  ...prev,
                  package_id: item.package_id,
                  package_service_id: item.package_service_id,
                  treatment_name: item.label,
                }));
                setPackageSearch(item.label);
                setPackageOpen(false);
              }}
              sessionFraction={form.session_fraction}
              onSessionFractionChange={(v) => set('session_fraction', v)}
              sessionFractionError={errors.session_fraction}
              medicineOil={form.medicine_oil}
              onMedicineOilChange={(v) => set('medicine_oil', v)}
              medicineOilError={errors.medicine_oil}
              notes={form.notes}
              onNotesChange={(v) => set('notes', v)}
              showOptionalFields={!hideOptionalBookingSections}
              headerAction={
                !hideOptionalBookingSections && (
                  showTreatment2 ? (
                    <button
                      type="button"
                      onClick={() => {
                        // Cancel doctor 1: promote doctor 2's details to the primary
                        // slot and drop the second card, leaving one doctor on the plan.
                        setForm((prev) => ({
                          ...prev,
                          doctor_id: treatment2.doctor_id,
                          package_id: treatment2.package_id,
                          package_service_id: treatment2.package_service_id,
                          session_fraction: treatment2.session_fraction,
                          medicine_oil: treatment2.medicine_oil,
                          notes: treatment2.notes,
                        }));
                        setPackageSearch(packageSearch2);
                        setShowTreatment2(false);
                        setTreatment2(emptyTreatment2);
                        setPackageSearch2('');
                      }}
                      title="Cancel this doctor & treatment"
                      style={iconButtonStyle}
                      onMouseEnter={handleIconBtnEnter}
                      onMouseLeave={handleIconBtnLeave}
                    >
                      <IconMinus />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowTreatment2(true)}
                      title="Add another doctor & treatment"
                      style={iconButtonStyle}
                      onMouseEnter={handleIconBtnEnter}
                      onMouseLeave={handleIconBtnLeave}
                    >
                      <IconPlus />
                    </button>
                  )
                )
              }
            />

            {showTreatment2 && !hideOptionalBookingSections && (
              <div style={{ marginTop: 12 }}>
                <TreatmentInfoCard
                  title="Doctor and treatment information (2)"
                  doctors={doctors}
                  doctorId={treatment2.doctor_id}
                  onDoctorChange={(v) => setTreatment2Field('doctor_id', v)}
                  doctorError={errors.treatment2_doctor_id}
                  doctorOptional={!isDoctorBookingMode(form.booking_mode)}
                  showViewSchedule={false}
                  packageSearch={packageSearch2}
                  onPackageSearchChange={(value) => {
                    setPackageSearch2(value);
                    setTreatment2((prev) => ({
                      ...prev,
                      package_id: '',
                      package_service_id: '',
                      treatment_name: value,
                    }));
                  }}
                  packageOpen={packageOpen2}
                  setPackageOpen={setPackageOpen2}
                  packageResults={packageResults2}
                  searchingPackage={searchingPackage2}
                  onSelectPackage={(item) => {
                    setTreatment2((prev) => ({
                      ...prev,
                      package_id: item.package_id,
                      package_service_id: item.package_service_id,
                      treatment_name: item.label,
                    }));
                    setPackageSearch2(item.label);
                    setPackageOpen2(false);
                  }}
                  sessionFraction={treatment2.session_fraction}
                  onSessionFractionChange={(v) => setTreatment2Field('session_fraction', v)}
                  sessionFractionError={errors.treatment2_session_fraction}
                  sessionFractionOptional
                  medicineOil={treatment2.medicine_oil}
                  onMedicineOilChange={(v) => setTreatment2Field('medicine_oil', v)}
                  notes={treatment2.notes}
                  onNotesChange={(v) => setTreatment2Field('notes', v)}
                  showOptionalFields
                  headerAction={
                    <button
                      type="button"
                      onClick={() => {
                        setShowTreatment2(false);
                        setTreatment2(emptyTreatment2);
                        setPackageSearch2('');
                      }}
                      title="Remove this card"
                      style={iconButtonStyle}
                      onMouseEnter={handleIconBtnEnter}
                      onMouseLeave={handleIconBtnLeave}
                    >
                      <IconMinus />
                    </button>
                  }
                />
              </div>
            )}

            {conflictMessage && (
              <div style={{
                background: '#fffbeb', border: '1px solid #fde68a',
                color: '#92400e', padding: '12px 16px', borderRadius: 10,
                marginBottom: 16, fontSize: 14,
              }}>
                ⚠️ {conflictMessage}
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 12,
              marginTop: 24, paddingTop: 16, borderTop: '1px solid #edf2f7',
            }}>
              <Button variant="secondary" onClick={() => history.goBack()}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={saving || loadingData || fetchingPlan}>
                {saving ? 'Saving…' : fetchingPlan ? 'Loading booking…' : isEdit ? 'Update Session' : 'Confirm Booking'}
              </Button>
            </div>
          </form>
        </div>

        {/* RIGHT — Availability Panel */}
        {/* <div className="availability-wrapper"> */}
        <div className="availability-wrapper" style={{ overflowY: 'auto', paddingRight: '12px', paddingBottom: '20px' }}>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
              Availability Checker
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
              Check doctor or room schedules for {form.start_date}
            </p>
          </div>

          <div style={{
            display: 'flex', gap: 6, background: '#f1f5f9',
            padding: 4, borderRadius: 10, marginBottom: 16,
          }}>
            {isAssignDoctorMode ? (
              <button key="doctor" type="button"
                onClick={() => setActivePanel((p) => (p === 'doctor' ? null : 'doctor'))}
                style={{
                  flex: 1, padding: '8px 12px', border: 'none', borderRadius: 8,
                  fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s',
                  background: activePanel === 'doctor' ? '#2563eb' : 'transparent',
                  color:      activePanel === 'doctor' ? '#fff'    : '#475569',
                }}>
                👨‍⚕️ Doctor/Therapist
              </button>
            ) : (
              ['doctor', 'room'].map((panel) => (
                <button key={panel} type="button"
                  onClick={() => setActivePanel((p) => (p === panel ? null : panel))}
                  style={{
                    flex: 1, padding: '8px 12px', border: 'none', borderRadius: 8,
                    fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s',
                    background: activePanel === panel ? '#2563eb' : 'transparent',
                    color:      activePanel === panel ? '#fff'    : '#475569',
                  }}>
                  {panel === 'doctor' ? '👨‍⚕️ Doctor/therapist' : '🏠 Room'}
                </button>
              ))
            )}
          </div>

          {activePanel === 'doctor' && (() => {
            const showSecondaryOption = showTreatment2 && treatment2.doctor_id;
            const viewingSecondary = checkerDoctorSlot === 'secondary' && showSecondaryOption;

            return (
              <>
                {showSecondaryOption && (
                  <div style={{ marginBottom: 12 }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: 4 }}>
                      Viewing schedule for
                    </label>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <select
                        value={viewingSecondary ? 'secondary' : 'primary'}
                        onChange={(e) => setCheckerDoctorSlot(e.target.value)}
                        style={{
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          border: 'none',
                          background: 'transparent',
                          padding: '0 20px 0 0',
                          margin: 0,
                          fontSize: 14,
                          fontWeight: 700,
                          color: '#0f172a',
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                      >
                        <option value="primary">
                          {selectedDoctorName || 'Doctor 1'}
                        </option>
                        <option value="secondary">
                          {selectedDoctorName2 || 'Doctor 2'}
                        </option>
                      </select>
                      <span
                        style={{
                          position: 'absolute',
                          right: 2,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          fontSize: 20,
                          color: '#475569',
                        }}
                      >
                        ▾
                      </span>
                    </div>
                  </div>
                )}

                <AvailabilityViewer
                  title={
                    viewingSecondary
                      ? (selectedDoctorName2 || 'Second doctor schedule')
                      : (selectedDoctorName || 'Select a doctor to view schedule')
                  }
                  schedule={viewingSecondary ? doctorSchedule2 : doctorSchedule}
                  loading={viewingSecondary ? loadingSchedule2 : loadingSchedule}
                  doctorId={viewingSecondary ? treatment2.doctor_id : form.doctor_id}
                  date={form.start_date}
                  selectedTime={form.start_time}
                  selectedEndTime={form.end_time}
                  currentBookingId={currentBookingId}
                  onSelect={
                    viewingSecondary
                      ? undefined
                      : (start, end) => { set('start_time', start); set('end_time', end); }
                  }
                />
              </>
            );
          })()}

          {activePanel === 'room' && (
            <MultiRoomAvailabilityTable
              date={form.start_date}
              totalDays={1}
              roomCategory={ROOM_CATEGORIES.TREATMENT_ROOM}
              selectedRoomId={form.room_id ? Number(form.room_id) : null}
              selectedTime={form.start_time}
              currentBookingId={currentBookingId}
              onSelectRoom={(roomId, start, end) => {
                set('room_id', roomId);
                set('start_time', start);
                set('end_time', end);
              }}
            />
          )}

          {!activePanel && (
            <div style={{
              padding: '40px 20px', textAlign: 'center',
              background: '#f8fafc', border: '1px dashed #cbd5e1',
              borderRadius: 12, color: '#94a3b8',
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
              <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>No panel selected</div>
              <div style={{ fontSize: 13 }}>Select a doctor or room above to check availability</div>
            </div>
          )}
        </div>
      </div>

      <PatientFormModal
        isOpen={showPatientForm}
        onClose={() => setShowPatientForm(false)}
        onPatientCreated={async (newPatient) => {
          const pRes = await bookingPlanService.getClosedVisitPatients();
          setPatients(asList(pRes.data));
          /*set('patient_id', newPatient.id);*/ 
          selectPatient(newPatient);   
        }}
      />
      
      {/* Error Modal for 420 Validation Errors */}
      {showErrorModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, width: 480,
            maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600, color: '#dc2626' }}>
                Cannot Create Booking Plan
              </h3>
              <p style={{ margin: '0 0 20px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
                {errorModalMessage}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button 
                variant="primary" 
                onClick={() => setShowErrorModal(false)}
                style={{ background: '#dc2626' }}
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
